import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import api, { formatApiError, fileUrl } from "@/lib/api";
import { PageHeader, ScoreBadge, StatusBadge, EmailFunnel } from "@/components/Common";
import AutopilotQueue from "@/components/AutopilotQueue";
import AIDiscoveryDialog from "@/components/AIDiscoveryDialog";
import { QualBadge, qualRank, DiscoveryReportPanel, QualificationEvidence } from "@/components/LeadQualification";
import { FinderOptionsDialog } from "@/components/FinderOptionsDialog";
import { ImportContactsDialog } from "@/components/ImportContactsDialog";
import { SortSelect, useSortOptions, sortRows, pickRecentId } from "@/components/SortSelect";
import ContactNowBanner from "@/components/ContactNowBanner";
import { EmailPreviewButton } from "@/components/EmailPreviewDialog";
import { EmailComposer, BatchCcBcc, AttachmentsInline, BatchAttachments } from "@/components/EmailComposer";import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import ErrorBoundary from "@/components/ErrorBoundary";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import {
  Radar, Loader2, Sparkles, Mail, Instagram, MapPin, Globe, Send, Copy, Check, Trash2,
  Download, Eye, Phone, Star, List, Map as MapIcon, Archive, Save, Clock, Users, Inbox, Plus, MessageCircle, Smartphone, Paperclip, X, RotateCcw, ChevronRight, Store, StickyNote, UserPlus, BadgeCheck, AlertTriangle, Shirt, Target, Settings2, Layers, FileSpreadsheet,
} from "lucide-react";
import { MapContainer, TileLayer, CircleMarker, Popup, useMap } from "react-leaflet";
import { REGIONS, PROVINCES_BY_REGION } from "@/lib/italy";
import { normalizePhoneE164 } from "@/lib/utils";
import { MessageBody } from "@/components/MessageBody";

const CATEGORIES = [
  { value: "bici", label: "Negozi abbigliamento e accessori bici" },
  { value: "moto", label: "Negozi Moto" },
  { value: "sport", label: "Sport premium" },
];
const NATIONS = ["Italia", "Germania", "Francia", "Spagna", "Regno Unito", "Stati Uniti", "Internazionale"];
const STATUSES = ["nuovo", "contattato", "in trattativa", "qualificato", "interessato", "non contattare", "scartato"];
const QUALITY_OPTIONS = [
  { value: "0", label: "Tutti i match" },
  { value: "70", label: "Match ≥ 70%" },
  { value: "80", label: "Match ≥ 80%" },
  { value: "90", label: "Match ≥ 90%" },
];

const WA_NATION_LANG = { Italia: "it", Francia: "fr", Spagna: "es", Germania: "de", Austria: "de" };
const waLangForNation = (nation) => WA_NATION_LANG[(nation || "Italia").trim()] || "en";
const WaLangBadge = ({ nation, override }) => {
  const code = override && override !== "auto" ? override : waLangForNation(nation);
  return (
    <span data-testid="recipient-lang-badge" title={`Lingua template: ${code.toUpperCase()}`}
      className="inline-flex items-center rounded-full border border-border bg-brema-card px-1.5 py-0.5 text-[10px] font-semibold uppercase text-muted-foreground">
      {code}
    </span>
  );
};

const extUrl = (u) => (u && !/^https?:\/\//i.test(u) ? `https://${u}` : u);
const igUrl = (h) => {
  if (!h) return "#";
  if (/^https?:\/\//i.test(h)) return h;
  return `https://instagram.com/${String(h).replace(/^@/, "")}`;
};
const markerColor = (score) => {
  const dark = typeof document !== "undefined" && document.documentElement.classList.contains("dark");
  if (dark) return score >= 85 ? "#22C55E" : score >= 70 ? "#FACC15" : "#EF4444";
  return score >= 85 ? "#9B2242" : score >= 70 ? "#1E3A5F" : "#94A3B8";
};
const fmtDateTime = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
};
const daysSince = (iso) => {
  if (!iso) return Infinity;
  const d = new Date(iso).getTime();
  if (isNaN(d)) return Infinity;
  return (Date.now() - d) / 86400000;
};

const DELIVERY_LABELS = { inviata: "Inviata", consegnata: "Consegnata", aperta: "Aperta", click: "Cliccata", risposta: "Risposta", rimbalzata: "Rimbalzata", reclamo: "Reclamo", "in ritardo": "In ritardo" };
const isPhoneRecipient = (v) => /^\+?[0-9][0-9\s().-]{5,}$/.test(String(v || "").trim());
const recipientHref = (v) => (isPhoneRecipient(v) ? `https://wa.me/${String(v).replace(/[^0-9]/g, "")}` : `mailto:${v}`);
const DELIVERY_RANK = { inviata: 1, consegnata: 2, aperta: 3, click: 4, risposta: 5 };
const TIMELINE_STEPS = [
  { key: "inviata", label: "Inviata" },
  { key: "consegnata", label: "Consegnata" },
  { key: "aperta", label: "Aperta" },
  { key: "click", label: "Cliccata" },
  { key: "risposta", label: "Risposta" },
];
const deliveryClass = (s) => {
  if (s === "risposta") return "bg-emerald-500/20 text-emerald-800 dark:text-emerald-200 border border-emerald-500/40";
  if (["aperta", "click"].includes(s)) return "bg-green-500/20 text-green-800 dark:text-green-200 border border-green-500/40";
  if (["rimbalzata", "reclamo"].includes(s)) return "bg-red-500/20 text-red-800 dark:text-red-200 border border-red-500/40";
  if (s === "consegnata") return "bg-sky-500/20 text-sky-800 dark:text-sky-200 border border-sky-500/40";
  if (s === "in ritardo") return "bg-amber-500/20 text-amber-900 dark:text-amber-200 border border-amber-500/40";
  return "bg-slate-500/20 text-slate-800 dark:text-slate-200 border border-slate-500/40";
};
const batchStatusClass = (s) => {
  if (s === "inviata") return "bg-green-500/20 text-green-800 dark:text-green-200 border border-green-500/40";
  if (s === "parziale") return "bg-amber-500/20 text-amber-900 dark:text-amber-200 border border-amber-500/40";
  return "bg-slate-500/20 text-slate-800 dark:text-slate-200 border border-slate-500/40";
};
const batchStatusLabel = (s) => (s === "inviata" ? "Inviata" : s === "parziale" ? "Parziale" : "Bozza");

const phoneDigits = (p) => (p || "").replace(/\D/g, "");
const phoneKind = (p) => {
  let d = phoneDigits(p);
  if (!d) return "unknown";
  if (d.startsWith("00")) d = d.slice(2);
  if (d.startsWith("39") && d.length > 9) d = d.slice(2);
  if (d.startsWith("3")) return "mobile";
  if (d.startsWith("0")) return "landline";
  return "unknown";
};
const toWaDigits = (p) => {
  let d = phoneDigits(p);
  if (!d) return "";
  if (d.startsWith("00")) d = d.slice(2);
  if (!(d.startsWith("39") && d.length >= 11) && d.length <= 10) d = "39" + d;
  return d;
};
const leadWaDigits = (lead) => {
  if (lead?.whatsapp) return toWaDigits(lead.whatsapp);
  if (lead?.phone && phoneKind(lead.phone) === "mobile") return toWaDigits(lead.phone);
  return "";
};
const isValidEmail = (e) => !!e && /^\S+@\S+\.\S+$/.test(String(e).trim());

// Formatta E.164 in versione leggibile con spazi (display only). Link/API restano puliti.
const E164_CC = ["351", "386", "385", "39", "49", "33", "34", "44", "41", "43", "31", "32", "1"];
const formatE164Display = (e164) => {
  if (!e164) return "";
  const s = String(e164).trim();
  if (!s.startsWith("+")) return s;
  const digits = s.slice(1).replace(/\D/g, "");
  if (!digits) return s;
  const cc = E164_CC.find((c) => digits.startsWith(c)) || digits.slice(0, 2);
  const nat = digits.slice(cc.length);
  if (!nat) return `+${cc}`;
  const g = cc === "39" ? 3 : 2;
  const first = nat.slice(0, g);
  const rest = nat.slice(g);
  return `+${cc} ${first}${rest ? " " + rest : ""}`;
};

function FitBounds({ points }) {
  const map = useMap();
  useEffect(() => {
    if (points.length === 1) map.setView(points[0], 12);
    else if (points.length > 1) map.fitBounds(points, { padding: [40, 40], maxZoom: 12 });
  }, [points, map]);
  return null;
}

function CopyBtn({ text, testid }) {
  const [copied, setCopied] = useState(false);
  return (
    <Button
      type="button" variant="secondary" size="sm" data-testid={testid}
      onClick={() => { navigator.clipboard.writeText(text || ""); setCopied(true); setTimeout(() => setCopied(false), 1500); }}
      className="rounded-full border border-border bg-transparent text-xs hover:bg-brema-hover"
    >
      {copied ? <Check className="mr-1 h-3.5 w-3.5" /> : <Copy className="mr-1 h-3.5 w-3.5" />}
      {copied ? "Copiato" : "Copia"}
    </Button>
  );
}

export default function SalesAgent() {
  // --- Search box (Ricerca autonoma) ---
  const [nation, setNation] = useState("Italia");
  const [region, setRegion] = useState("Lombardia");
  const [province, setProvince] = useState("tutte");
  const [city, setCity] = useState("");
  const [radius, setRadius] = useState(25);
  const [category, setCategory] = useState("bici");
  const [categories, setCategories] = useState(CATEGORIES);
  const [catMgr, setCatMgr] = useState(false);
  const loadCategories = () => api.get("/admin/finder-options?group=sales_category").then(({ data }) => { if (data.options?.length) setCategories(data.options); }).catch(() => {});
  useEffect(() => { loadCategories(); }, []);
  const [count, setCount] = useState(5);
  const [minScoreSearch, setMinScoreSearch] = useState("0");
  const [contactFilter, setContactFilter] = useState("any");
  const [discoveryOpen, setDiscoveryOpen] = useState(false);
  const [discovering, setDiscovering] = useState(false);
  const [discoverProgress, setDiscoverProgress] = useState(null);
  const [discoverReport, setDiscoverReport] = useState(null);
  const [integrations, setIntegrations] = useState(null);
  const [searchResults, setSearchResults] = useState([]);
  const [qualFilter, setQualFilter] = useState("all");
  const [brandsOpen, setBrandsOpen] = useState(false);
  const [brands, setBrands] = useState([]);
  const [newBrand, setNewBrand] = useState("");
  const [newBrandAmb, setNewBrandAmb] = useState(false);
  const [excluded, setExcluded] = useState([]);
  const [loadingExcluded, setLoadingExcluded] = useState(false);
  const [resultSel, setResultSel] = useState({});
  const [savingResults, setSavingResults] = useState(false);
  const [savingNotes, setSavingNotes] = useState(false);
  const [savingContacts, setSavingContacts] = useState(false);
  const isItaly = nation === "Italia";

  // --- Archive (rivenditori) ---
  const [searchParams] = useSearchParams();
  const [leads, setLeads] = useState([]);
  const [filterNation, setFilterNation] = useState("tutte");
  const [filterCity, setFilterCity] = useState("");
  const [minScore, setMinScore] = useState("0");
  const [filterStatus, setFilterStatus] = useState(() => searchParams.get("status") || "tutti");
  const [sortBy, setSortBy] = useState("");
  const { options: sortOptions, catalog: sortCatalog } = useSortOptions("rivenditori");
  const [viewMode, setViewMode] = useState("list");
  const [mapReady, setMapReady] = useState(false);
  const [tab, setTab] = useState(() => searchParams.get("tab") || "rivenditori");
  // Risincronizza tab/stato ad ogni cambio di query param (es. drill-down ripetuti dal Command Center)
  useEffect(() => {
    const tp = searchParams.get("tab");
    const sp = searchParams.get("status");
    if (tp) setTab(tp);
    if (sp) setFilterStatus(sp);
  }, [searchParams]);

  // --- Selection / bulk ---
  const [selected, setSelected] = useState({});
  const [tone, setTone] = useState("professionale");
  const [bulkChannel, setBulkChannel] = useState("email");
  const [bulkAssets, setBulkAssets] = useState([]);
  const [bulkWaDoc, setBulkWaDoc] = useState(null);
  const [bulkWaTplId, setBulkWaTplId] = useState("none");
  const [channelView, setChannelView] = useState("all");
  const emptyTpl = { title: "", meta_template_name: "", language: "it", default_link: "", preview_text: "" };
  const [waTemplates, setWaTemplates] = useState([]);
  const [tplManagerOpen, setTplManagerOpen] = useState(false);
  const [tplForm, setTplForm] = useState(emptyTpl);
  const [tplEditingId, setTplEditingId] = useState(null);
  const [tplSaving, setTplSaving] = useState(false);
  const [notContactedDays, setNotContactedDays] = useState(90);
  const [bulkRunning, setBulkRunning] = useState(false);

  // --- Bulk composer (Messaggio Unico / AI) + Libreria Modelli ---
  const [bulkComposerOpen, setBulkComposerOpen] = useState(false);
  const [bulkMode, setBulkMode] = useState("unique"); // unique | ai
  const [bulkSubject, setBulkSubject] = useState("");
  const [bulkBody, setBulkBody] = useState("");
  const [bulkLink, setBulkLink] = useState("");
  const [bulkLang, setBulkLang] = useState("auto");
  const [extraWaRecipients, setExtraWaRecipients] = useState([]);
  const [newRecip, setNewRecip] = useState({ name: "", city: "", phone: "" });
  const [bulkSending, setBulkSending] = useState(false);
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [saveTplOpen, setSaveTplOpen] = useState(false);
  const [saveTplTitle, setSaveTplTitle] = useState("");
  const [saveTplSaving, setSaveTplSaving] = useState(false);
  const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false);
  const [recipientsOpen, setRecipientsOpen] = useState(false);
  const [deleteSelOpen, setDeleteSelOpen] = useState(false);
  const [deleteSelConfirm, setDeleteSelConfirm] = useState("");
  const [leadToDelete, setLeadToDelete] = useState(null);
  const [deletingOne, setDeletingOne] = useState(false);
  const [deletingSel, setDeletingSel] = useState(false);
  const [bulkCc, setBulkCc] = useState("");
  const [bulkBcc, setBulkBcc] = useState("");
  const [bulkCcOpen, setBulkCcOpen] = useState(false);
  const [bulkAttachments, setBulkAttachments] = useState([]);
  const [msgCcOpen, setMsgCcOpen] = useState({});

  // --- Single outreach dialog (unified composer) ---
  const [dialogLead, setDialogLead] = useState(null);
  const [justArchived, setJustArchived] = useState([]);
  const contactNext = () => {
    setJustArchived((prev) => {
      if (!prev.length) return prev;
      const [first, ...rest] = prev;
      setDialogLead(first);
      return rest;
    });
  };

  // --- Detail dialog ---
  const [detailLead, setDetailLead] = useState(null);
  const [savingWa, setSavingWa] = useState(false);

  // --- Campaigns / batches ---
  const [batches, setBatches] = useState([]);
  const [activeBatch, setActiveBatch] = useState(null);
  const [batchMessages, setBatchMessages] = useState([]);
  const [batchLoading, setBatchLoading] = useState(false);
  const [batchSending, setBatchSending] = useState(false);

  // --- History ---
  const [history, setHistory] = useState([]);
  const [historyDetail, setHistoryDetail] = useState(null);
  const [historySel, setHistorySel] = useState(new Set());
  const [histDelOpen, setHistDelOpen] = useState(false);
  const [deletingHist, setDeletingHist] = useState(false);

  // --- Manual contact addition ---
  const emptyManualLead = { name: "", email: "", city: "", region: "", nation: "Italia", category: "bici", phone: "", whatsapp: "", website: "", instagram: "", notes: "" };
  const [manualOpen, setManualOpen] = useState(false);
  const [manualSaving, setManualSaving] = useState(false);
  const [manualLead, setManualLead] = useState(emptyManualLead);
  const setML = (patch) => setManualLead((p) => ({ ...p, ...patch }));
  const [lookupName, setLookupName] = useState("");
  const [lookingUp, setLookingUp] = useState(false);

  const loadLeads = () => api.get("/leads").then(({ data }) => setLeads(data.leads || [])).catch(() => {});
  const loadBatches = () => api.get("/campaign-batches?target=leads").then(({ data }) => setBatches(data.batches || [])).catch(() => {});
  const loadHistory = () => api.get("/campaigns/history?target=leads").then(({ data }) => setHistory(data.history || [])).catch(() => {});
  const loadWaTemplates = () => api.get("/message-templates?channel=whatsapp").then(({ data }) => setWaTemplates(data.templates || [])).catch(() => {});
  const loadEmailTemplates = () => api.get("/send-templates?module=rivenditori").then(({ data }) => setEmailTemplates(data.templates || [])).catch(() => {});

  const [dedupingLeads, setDedupingLeads] = useState(false);
  const [cleaningFalse, setCleaningFalse] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const dedupLeads = async () => {
    if (!window.confirm("Unire le schede doppie dello stesso negozio (stesso dominio sito)? I dati vengono uniti nel record più completo.")) return;
    setDedupingLeads(true);
    try {
      const { data } = await api.post("/admin/leads/dedup");
      toast.success(data.merged > 0 ? `${data.merged} schede duplicate unite` : "Nessun duplicato trovato");
      loadLeads();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Deduplica fallita"); }
    finally { setDedupingLeads(false); }
  };
  const cleanFalseSends = async () => {
    if (!window.confirm("Ripristinare le email segnate 'Inviata' ma senza un destinatario reale? Verranno rimosse dallo storico e riportate a bozza/nuovo.")) return;
    setCleaningFalse(true);
    try {
      const { data } = await api.post("/admin/campaigns/clean-false-sends");
      const tot = (data.campaigns_reset || 0) + (data.media_reset || 0) + (data.leads_reset || 0);
      toast.success(tot > 0 ? `Puliti ${data.campaigns_reset} invii falsi · ${data.leads_reset} rivenditori ripristinati` : "Nessun invio falso trovato");
      loadHistory(); loadLeads(); loadBatches();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Pulizia fallita"); }
    finally { setCleaningFalse(false); }
  };

  useEffect(() => {
    loadLeads(); loadBatches(); loadHistory(); loadWaTemplates(); loadEmailTemplates();
    api.get("/integrations/status").then(({ data }) => setIntegrations(data)).catch(() => {});
    api.get("/marketing-assets").then(({ data }) => setBulkAssets((data.assets || []).filter((a) => a.storage_path || a.file_path))).catch(() => {});
  }, []);
  useEffect(() => { setMapReady(true); }, []);
  // Sicurezza: azzera la selezione quando cambia un filtro (evita invii accidentali)
  useEffect(() => { setSelected({}); }, [minScore, filterNation, filterCity, filterStatus, channelView]);

  const pollDiscoverJob = (jobId) => new Promise((resolve, reject) => {
    let tries = 0;
    const iv = setInterval(async () => {
      tries += 1;
      try {
        const { data } = await api.get(`/discover-jobs/${jobId}`);
        const j = data.job;
        if (j.progress) {
          setDiscoverProgress(j.progress);
          const pv = j.progress.preview_leads;
          if (Array.isArray(pv) && pv.length) {
            setSearchResults((prev) => {
              const have = new Set(prev.map((r) => r.temp_id));
              const fresh = pv.filter((l) => l.temp_id && !have.has(l.temp_id));
              return fresh.length ? [...prev, ...fresh] : prev;
            });
          }
        }
        if (j.status === "ready") { clearInterval(iv); resolve(j.result || {}); }
        else if (j.status === "error") { clearInterval(iv); reject(new Error(j.error || "Ricerca fallita")); }
        else if (tries > 420) { clearInterval(iv); reject(new Error("La ricerca sta impiegando troppo tempo. Riprova.")); }
      } catch (e) { if (tries > 420) { clearInterval(iv); reject(e); } }
    }, 2500);
  });

  const discover = async () => {
    setDiscovering(true);
    setDiscoverProgress({ phase: "starting" });
    try {
      const excludeIds = searchResults.map((r) => r.temp_id).filter(Boolean);
      const payload = isItaly
        ? { nation, region: region === "tutte" ? "" : region, province: province === "tutte" ? "" : province, category, count: Number(count), min_score: Number(minScoreSearch), contact_filter: contactFilter, exclude_ids: excludeIds }
        : { nation, city: city.trim(), radius: Number(radius) || undefined, category, count: Number(count), min_score: Number(minScoreSearch), contact_filter: contactFilter, exclude_ids: excludeIds };
      const where = isItaly
        ? (province !== "tutte" ? `${province} (${region})` : (region === "tutte" ? "Tutte le regioni" : region))
        : (city.trim() ? `${city.trim()}, ${nation}` : nation);
      const { data: init } = await api.post("/leads/discover-async", payload);
      const result = await pollDiscoverJob(init.job_id);
      const leads = result.leads || [];
      const newCount = result.new_count ?? leads.filter((l) => !l.already_saved).length;
      const savedCount = leads.length - newCount;
      // Accumulo + upsert: i risultati finali (arricchiti) sovrascrivono la preview;
      // vengono rimosse le anteprime di QUESTA ricerca non confermate nella lista finale.
      setSearchResults((prev) => {
        const finalIds = new Set(leads.map((l) => l.temp_id));
        const byId = new Map(prev.map((r) => [r.temp_id, r]));
        for (const l of leads) byId.set(l.temp_id, { ...(byId.get(l.temp_id) || {}), ...l, enriching: false });
        return Array.from(byId.values()).filter((r) => !(r.enriching === true && !finalIds.has(r.temp_id)));
      });
      const req = result.requested || Number(count);
      const exhausted = result.exhausted;
      if (result.report) setDiscoverReport(result.report);
      if (result.engine === "brema-qualifier") {
        const q = result.qualified_count ?? newCount, v = result.to_verify_count ?? 0, ex = result.excluded_count ?? 0;
        if (leads.length === 0) {
          toast.info(`Nessun rivenditore qualificato (≥70) in ${where}: ${result.report?.analyzed ?? 0} candidati analizzati, ${ex} esclusi.`);
        } else {
          let msg = `${q}/${req} rivenditori qualificati (≥70) · ${where}`;
          if (v) msg += ` · ${v} da verificare (in coda, non conteggiati)`;
          if (ex) msg += ` · ${ex} esclusi`;
          if (q < req) msg += result.report?.limit_reached ? ` · limite ${result.report.max_analyzed} candidati analizzati` : exhausted ? " · zona esaurita" : "";
          (q >= req ? toast.success : toast.info)(msg);
        }
      } else if (leads.length === 0) {
        toast.info(exhausted
          ? `Nessun altro contatto valido in ${where}: la zona è esaurita per questo filtro.`
          : `Nessun nuovo rivenditore trovato in ${where}. Prova un'altra zona o filtro.`);
      } else {
        let msg = `+${newCount} nuovi rivenditori contattabili · ${where}`;
        if (savedCount) msg += ` · ${savedCount} già in archivio`;
        if (newCount < req) msg += exhausted ? ` · zona esaurita (${newCount}/${req})` : ` (${newCount}/${req} richiesti)`;
        toast.success(msg);
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setDiscovering(false);
      setDiscoverProgress(null);
    }
  };

  const resetFilters = () => {
    setFilterNation("tutte"); setFilterCity(""); setMinScore("0"); setFilterStatus("tutti"); setChannelView("all");
    toast.success("Filtri di ricerca azzerati");
  };
  const showImported = () => {
    setFilterNation("tutte"); setFilterCity(""); setMinScore("0"); setFilterStatus("tutti"); setChannelView("all");
    setSortBy(pickRecentId(sortOptions)); setViewMode("list"); setTab("rivenditori");
    loadLeads();
    setTimeout(() => document.querySelector('[data-testid="leads-table-wrap"]')?.scrollIntoView({ behavior: "smooth", block: "start" }), 300);
    toast.success("Archivio Rivenditori aggiornato: i nuovi contatti sono in cima alla lista");
  };
  const resetSearch = () => {
    setNation("Italia"); setRegion("Lombardia"); setProvince("tutte");
    setCity(""); setRadius(25); setCategory("bici"); setMinScoreSearch("0"); setCount(5);
    setSearchResults([]); setResultSel({});
    toast.success("Ricerca azzerata");
  };

  const toggleResultSel = (id) => setResultSel((p) => ({ ...p, [id]: !p[id] }));

  const loadBrands = async () => {
    try { const { data } = await api.get("/apparel-brands"); setBrands(data.brands || []); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Errore caricamento marchi"); }
  };
  const openBrands = () => { setBrandsOpen(true); loadBrands(); };
  const addBrand = async () => {
    const name = newBrand.trim();
    if (!name) return;
    try {
      await api.post("/apparel-brands", { name, ambiguous: newBrandAmb });
      setNewBrand(""); setNewBrandAmb(false);
      toast.success(`Marchio "${name}" aggiunto`);
      loadBrands();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Errore aggiunta marchio"); }
  };
  const deleteBrand = async (b) => {
    try { await api.delete(`/apparel-brands/${b._id}`); toast.success(`"${b.name}" rimosso`); loadBrands(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Errore rimozione"); }
  };

  const loadExcluded = async () => {
    setLoadingExcluded(true);
    try { const { data } = await api.get("/excluded-leads"); setExcluded(data.leads || []); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Errore caricamento esclusi"); }
    finally { setLoadingExcluded(false); }
  };
  const deleteExcluded = async (id) => {
    try { await api.delete(`/excluded-leads/${id}`); setExcluded((p) => p.filter((x) => x._id !== id)); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Errore"); }
  };
  const clearExcluded = async () => {
    if (!window.confirm("Svuotare l'archivio degli esclusi?")) return;
    try { const { data } = await api.post("/excluded-leads/clear"); toast.success(`${data.deleted} esclusi rimossi`); setExcluded([]); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Errore"); }
  };

  const toggleAllResults = () => {
    const saveable = searchResults.filter((r) => !r.already_saved);
    const allOn = saveable.length > 0 && saveable.every((r) => resultSel[r.temp_id]);
    if (allOn) setResultSel({});
    else { const next = {}; saveable.forEach((r) => { next[r.temp_id] = true; }); setResultSel(next); }
  };
  const saveResults = async (list) => {
    const toSave = (list || []).filter(Boolean);
    if (!toSave.length) { toast.error("Nessun negozio da salvare"); return; }
    setSavingResults(true);
    try {
      const { data } = await api.post("/leads/save", { leads: toSave });
      const parts = [];
      if (data.created) parts.push(`${data.created} salvati`);
      if (data.updated) parts.push(`${data.updated} aggiornati`);
      toast.success(`${parts.join(" · ") || "0 salvati"} in archivio`);
      const keys = new Set(toSave.map((l) => l.temp_id));
      setSearchResults((prev) => prev.map((r) => (keys.has(r.temp_id) ? { ...r, already_saved: true } : r)));
      setResultSel({});
      await loadLeads();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setSavingResults(false); }
  };
  const saveOneResult = (r) => saveResults([r]);
  const saveSelectedResults = () => saveResults(searchResults.filter((r) => resultSel[r.temp_id]));
  const saveAllResults = () => saveResults(searchResults.filter((r) => !r.already_saved));

  const outreachFromResult = async (r) => {
    try {
      let leadDoc = r.already_saved ? (leads.find((l) => l.dedup_key === r.dedup_key) || null) : null;
      if (!leadDoc) {
        const { data } = await api.post("/leads/save", { leads: [r] });
        leadDoc = (data.leads || [])[0] || null;
        setSearchResults((prev) => prev.map((x) => (x.temp_id === r.temp_id ? { ...x, already_saved: true } : x)));
        await loadLeads();
        toast.success("Salvato in archivio · apertura outreach");
      }
      if (leadDoc) { setDetailLead(null); openOutreach(leadDoc); }
      else toast.error("Impossibile preparare l'outreach");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };

  // Invio di gruppo direttamente dai risultati di ricerca (salvataggio trasparente, nessun passaggio manuale)
  const outreachSelectedResults = async () => {
    const chosen = searchResults.filter((r) => resultSel[r.temp_id]);
    if (!chosen.length) { toast.error("Nessun rivenditore selezionato"); return; }
    setSavingResults(true);
    try {
      const { data } = await api.post("/leads/save", { leads: chosen });
      const savedLeads = data.leads || [];
      const keys = new Set(chosen.map((l) => l.temp_id));
      setSearchResults((prev) => prev.map((r) => (keys.has(r.temp_id) ? { ...r, already_saved: true } : r)));
      await loadLeads();
      const sel = {};
      savedLeads.forEach((l) => { if (l._id) sel[l._id] = true; });
      if (!Object.keys(sel).length) { toast.error("Impossibile preparare l'invio"); return; }
      setSelected(sel);
      setResultSel({});
      // apre direttamente il compositore massivo (bypassa la guardia selectedCount)
      setBulkMode("unique");
      setBulkCc(""); setBulkBcc(""); setBulkCcOpen(false); setBulkAttachments([]);
      if (bulkChannel === "whatsapp") {
        const tpl = waTemplates.find((t) => t._id === bulkWaTplId);
        setBulkBody(tpl?.preview_text || tpl?.body || "");
        setBulkLink(tpl?.default_link || integrations?.whatsapp_catalog_link || "");
      }
      setBulkComposerOpen(true);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setSavingResults(false); }
  };

  const saveLeadNotes = async () => {
    if (!detailLead?._id) return;
    setSavingNotes(true);
    try {
      await api.patch(`/leads/${detailLead._id}`, { notes: detailLead.notes || "" });
      toast.success("Note salvate");
      setLeads((prev) => prev.map((l) => (l._id === detailLead._id ? { ...l, notes: detailLead.notes } : l)));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setSavingNotes(false); }
  };
  const saveLeadContacts = async () => {
    if (!detailLead?._id) return;
    setSavingContacts(true);
    try {
      const payload = {
        email: detailLead.email || "",
        phone: detailLead.phone || "",
        website: detailLead.website || "",
        contact_ref: detailLead.contact_ref || "",
      };
      await api.patch(`/leads/${detailLead._id}`, payload);
      toast.success("Contatti aggiornati");
      setLeads((prev) => prev.map((l) => (l._id === detailLead._id ? { ...l, ...payload } : l)));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setSavingContacts(false); }
  };
  const deleteSelected = async () => {
    const ids = visibleLeads.filter((l) => selected[l._id]).map((l) => l._id);
    if (!ids.length) { toast.error("Nessun rivenditore selezionato"); return; }
    setDeletingSel(true);
    try {
      const { data } = await api.post("/leads/delete-selected", { lead_ids: ids });
      toast.success(`${data.deleted} rivenditori eliminati`);
      setDeleteSelOpen(false); setDeleteSelConfirm(""); setSelected({});
      await loadLeads();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setDeletingSel(false); }
  };

  const deleteOneLead = async () => {
    if (!leadToDelete?._id) return;
    setDeletingOne(true);
    try {
      await api.post("/leads/delete-selected", { lead_ids: [leadToDelete._id] });
      toast.success("Rivenditore eliminato dall'archivio");
      if (detailLead?._id === leadToDelete._id) setDetailLead(null);
      setSelected((s) => { const n = { ...s }; delete n[leadToDelete._id]; return n; });
      setLeadToDelete(null);
      await loadLeads();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setDeletingOne(false); }
  };

  const toggleHistSel = (id) => setHistorySel((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleHistAll = () => setHistorySel((s) => (s.size === history.length ? new Set() : new Set(history.map((m) => m._id))));
  const deleteHistorySelected = async () => {
    const ids = [...historySel];
    if (!ids.length) return;
    setDeletingHist(true);
    try {
      const { data } = await api.post("/campaigns/history/delete-selected", { ids });
      toast.success(`${data.deleted} invii eliminati`);
      if (historyDetail && ids.includes(historyDetail._id)) setHistoryDetail(null);
      setHistorySel(new Set());
      setHistDelOpen(false);
      await loadHistory();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setDeletingHist(false); }
  };

  const runLeadLookup = async () => {
    const q = lookupName.trim();
    if (!q) { toast.error("Digita il nome del rivenditore da cercare"); return; }
    setLookingUp(true);
    try {
      const { data } = await api.post("/leads/lookup", { name: q, nation: manualLead.nation || "Italia", category: manualLead.category || "bici" });
      const p = data.preview || {};
      setML({ name: p.name || q, email: p.email || "", phone: p.phone || "", whatsapp: p.whatsapp || "", website: p.website || "", instagram: p.instagram || "", city: p.city || "", region: p.region || "", nation: p.nation || manualLead.nation, category: p.category || manualLead.category, notes: p.notes || "" });
      if (data.found) toast.success("Dati compilati dall'AI · controlla e salva");
      else toast("Nessun dato trovato online · compila manualmente");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setLookingUp(false); }
  };

  const submitManualLead = async () => {
    if (!manualLead.name.trim()) { toast.error("Il nome del rivenditore è obbligatorio"); return; }
    setManualSaving(true);
    try {
      const { data } = await api.post("/leads/manual", manualLead);
      toast.success(data.created ? "Rivenditore aggiunto alla lista" : (data.note || "Rivenditore già presente"));
      setManualOpen(false);
      setManualLead(emptyManualLead);
      setTab("rivenditori");
      await loadLeads();
      if (data.created && data.lead) setJustArchived([data.lead]);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setManualSaving(false); }
  };

  const exportCsv = async () => {
    try {
      const res = await api.get("/leads/export", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      const cd = res.headers["content-disposition"] || "";
      const match = cd.match(/filename="?([^"]+)"?/);
      a.download = match ? match[1] : "brema_rivenditori.csv";
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("CSV esportato");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const changeStatus = async (lead, status) => {
    setLeads((prev) => prev.map((l) => (l._id === lead._id ? { ...l, status } : l)));
    try { await api.patch(`/leads/${lead._id}`, { status }); }
    catch { toast.error("Aggiornamento fallito"); loadLeads(); }
  };

  const saveLeadWhatsapp = async () => {
    if (!detailLead?._id) return;
    setSavingWa(true);
    try {
      await api.patch(`/leads/${detailLead._id}`, { whatsapp: detailLead.whatsapp || "" });
      toast.success("Numero WhatsApp salvato");
      setLeads((prev) => prev.map((l) => (l._id === detailLead._id ? { ...l, whatsapp: detailLead.whatsapp } : l)));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setSavingWa(false); }
  };

  const saveTpl = async () => {
    if (!tplForm.title.trim()) { toast.error("Inserisci un titolo per il modello"); return; }
    setTplSaving(true);
    try {
      const payload = { ...tplForm, channel: "whatsapp", body: tplForm.preview_text };
      if (tplEditingId) await api.patch(`/message-templates/${tplEditingId}`, payload);
      else await api.post("/message-templates", payload);
      toast.success("Modello salvato");
      setTplForm(emptyTpl); setTplEditingId(null);
      loadWaTemplates();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setTplSaving(false); }
  };
  const editTpl = (t) => { setTplEditingId(t._id); setTplForm({ title: t.title || "", meta_template_name: t.meta_template_name || "", language: t.language || "it", default_link: t.default_link || "", preview_text: t.preview_text || t.body || "" }); };
  const deleteTpl = async (t) => {
    try { await api.delete(`/message-templates/${t._id}`); toast.success("Modello eliminato"); if (tplEditingId === t._id) { setTplEditingId(null); setTplForm(emptyTpl); } loadWaTemplates(); }
    catch { toast.error("Eliminazione fallita"); }
  };

  // --- single outreach ---
  const openOutreach = (lead) => setDialogLead(lead);

  // --- bulk ---
  const runBulkOutreach = async () => {
    const ids = bulkRecipients.map((l) => l._id);
    if (!ids.length) return;
    setBulkRunning(true);
    try {
      const tpl = waTemplates.find((t) => t._id === bulkWaTplId);
      const { data } = await api.post("/campaigns/bulk-generate", {
        lead_ids: ids, tone, channel: bulkChannel,
        wa_document: bulkChannel === "whatsapp" ? bulkWaDoc : null,
        wa_template: bulkChannel === "whatsapp" ? (tpl?.meta_template_name || null) : null,
        wa_preview: bulkChannel === "whatsapp" ? (tpl?.preview_text || null) : null,
      });
      if (bulkChannel === "whatsapp") {
        const noPhone = data.no_phone || 0;
        toast.success(`Campagna WhatsApp preparata${noPhone ? ` · ${noPhone} senza numero valido` : ""}. Controlla i messaggi nell'Archivio`);
      } else {
        const skip = data.recipients_without_email || data.skipped || 0;
        toast.success(`Generazione bozze completata${skip ? ` · ${skip} esclusi senza email` : ""}. Controlla i testi nell'Archivio`);
      }
      setSelected({});
      setTab("campagne");
      loadBatches();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Campagna fallita");
    } finally {
      setBulkRunning(false);
    }
  };

  // --- bulk composer (Messaggio Unico / AI + Libreria Modelli) ---
  const openBulkComposer = () => {
    if (selectedCount === 0) return;
    setBulkMode("unique");
    setBulkCc(""); setBulkBcc(""); setBulkCcOpen(false); setBulkAttachments([]);
    setExtraWaRecipients([]); setNewRecip({ name: "", city: "", phone: "" }); setBulkLang("auto");
    if (bulkChannel === "whatsapp") {
      const tpl = waTemplates.find((t) => t._id === bulkWaTplId);
      setBulkBody(tpl?.preview_text || tpl?.body || "");
      setBulkLink(tpl?.default_link || integrations?.whatsapp_catalog_link || "");
    }
    setBulkComposerOpen(true);
  };
  const applyEmailTemplate = (id) => {
    const t = emailTemplates.find((x) => x._id === id);
    if (t) { setBulkSubject(t.subject || ""); setBulkBody(t.body || ""); if (Array.isArray(t.attachments)) setBulkAttachments(t.attachments); }
  };
  const applyWaTemplate = (id) => {
    setBulkWaTplId(id);
    const t = waTemplates.find((x) => x._id === id);
    if (t) { setBulkBody(t.preview_text || t.body || ""); if (t.default_link) setBulkLink(t.default_link); }
  };
  const runBulkUnique = async () => {
    const ids = bulkRecipients.map((l) => l._id);
    const extras = bulkChannel === "whatsapp" ? extraWaRecipients : [];
    if (!ids.length && !extras.length) return;
    if (bulkChannel === "email" && !bulkBody.trim()) { toast.error("Scrivi il testo del messaggio"); return; }
    setBulkSending(true);
    try {
      const tpl = waTemplates.find((t) => t._id === bulkWaTplId);
      const payload = bulkChannel === "whatsapp"
        ? { lead_ids: ids, channel: "whatsapp", wa_template: tpl?.meta_template_name || null, wa_preview: bulkBody || null, wa_document: bulkWaDoc, wa_link: bulkLink || null, wa_lang: bulkLang === "auto" ? "" : bulkLang, extra_recipients: extras }
        : { lead_ids: ids, channel: "email", subject: bulkSubject, body: bulkBody, cc: bulkCc, bcc: bulkBcc, attachments: bulkAttachments };
      const { data } = await api.post("/campaigns/bulk-send", payload);
      const skipped = data.skipped || 0;
      const errs = data.errors || 0;
      const kind = bulkChannel === "whatsapp" ? "numero WhatsApp" : "email valida";
      if ((data.sent || 0) === 0) {
        toast.error(`Nessun messaggio inviato${errs ? ` · ${errs} falliti` : ""}${skipped ? ` · ${skipped} saltati (senza ${kind})` : ""}. Verifica numeri e configurazione.`, { duration: 9000 });
      } else if (errs > 0) {
        toast.warning(`Inviati ${data.sent} · ${errs} NON inviati (numero non valido/rifiutato)${skipped ? ` · ${skipped} saltati (senza ${kind})` : ""}. Controlla lo stato in Messaggi.`, { duration: 9000 });
      } else {
        toast.success(`Campagna inviata a ${data.sent} destinatari${skipped ? ` · ${skipped} saltati (senza ${kind})` : ""}`);
      }
      setBulkConfirmOpen(false); setBulkComposerOpen(false); setSelected({}); setBulkSubject(""); setBulkBody("");
      setExtraWaRecipients([]); setNewRecip({ name: "", city: "", phone: "" });
      setTab("storico"); loadBatches(); loadHistory(); loadLeads();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Invio fallito");
    } finally { setBulkSending(false); }
  };
  const runBulkAi = async () => {
    await runBulkOutreach();
    setBulkComposerOpen(false);
  };
  const saveCurrentAsTemplate = async () => {
    if (!saveTplTitle.trim()) { toast.error("Dai un titolo al modello"); return; }
    setSaveTplSaving(true);
    try {
      const tpl = waTemplates.find((t) => t._id === bulkWaTplId);
      const payload = bulkChannel === "whatsapp"
        ? { title: saveTplTitle, channel: "whatsapp", body: bulkBody, meta_template_name: tpl?.meta_template_name || "", default_link: bulkLink }
        : { title: saveTplTitle, channel: "email", subject: bulkSubject, body: bulkBody };
      await api.post("/message-templates", payload);
      toast.success("Modello salvato in Libreria");
      setSaveTplOpen(false); setSaveTplTitle("");
      loadEmailTemplates(); loadWaTemplates();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Salvataggio fallito");
    } finally { setSaveTplSaving(false); }
  };

  // --- batch detail ---
  const openBatch = async (b) => {
    setActiveBatch(b); setBatchMessages([]); setBatchLoading(true);
    try {
      const { data } = await api.get(`/campaign-batches/${b._id}`);
      setActiveBatch(data.batch);
      setBatchMessages((data.messages || []).map((m) => ({ ...m })));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setBatchLoading(false); }
  };
  const updateMsgField = (idx, field, val) =>
    setBatchMessages((prev) => prev.map((m, i) => (i === idx ? { ...m, [field]: val } : m)));
  const updateWaParam = (idx, pos, val) =>
    setBatchMessages((prev) => prev.map((m, i) => {
      if (i !== idx) return m;
      const params = [...(m.wa_params || ["", ""])];
      params[pos] = val;
      return { ...m, wa_params: params };
    }));
  const msgPayload = (m) => (m.channel === "whatsapp"
    ? { wa_phone: m.wa_phone || m.recipient, wa_params: m.wa_params || [] }
    : { subject: m.subject, email_body: m.email_body, social_dm: m.social_dm, cc: m.cc || "", bcc: m.bcc || "" });
  const saveMsg = async (m) => {
    try {
      await api.patch(`/campaigns/${m._id}`, msgPayload(m));
      toast.success("Bozza salvata");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };
  const sendMsg = async (m, idx) => {
    try {
      await api.patch(`/campaigns/${m._id}`, msgPayload(m));
      const { data } = await api.post(`/campaigns/${m._id}/send`);
      const dest = m.recipient || m.wa_phone || m.lead_name;
      toast.success(data.sent
        ? `${m.channel === "whatsapp" ? "WhatsApp inviato a" : "Email inviata a"} ${dest}`
        : (data.note || "Segnata come inviata"));
      updateMsgField(idx, "status", "inviata");
      loadBatches(); loadHistory(); loadLeads();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Invio fallito"); }
  };
  const removeFromBatch = async (m) => {
    if (m.status === "inviata") return;
    if (!window.confirm(`Rimuovere la bozza per "${m.lead_name || m.recipient}" da questa campagna?`)) return;
    try {
      await api.delete(`/campaigns/${m._id}`);
      setBatchMessages((prev) => prev.filter((x) => x._id !== m._id));
      setActiveBatch((b) => (b ? { ...b, recipient_count: Math.max(0, (b.recipient_count || 1) - 1) } : b));
      toast.success("Bozza rimossa dalla campagna");
      loadBatches();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };
  const sendWholeBatch = async () => {
    if (!activeBatch?._id) return;
    if (!window.confirm("Inviare tutte le bozze rimanenti di questa campagna?")) return;
    setBatchSending(true);
    try {
      const { data } = await api.post(`/campaign-batches/${activeBatch._id}/send`);
      toast.success(`${data.sent}/${data.total} email inviate${data.errors ? ` · ${data.errors} errori` : ""}`);
      await openBatch(activeBatch);
      loadBatches(); loadHistory(); loadLeads();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Invio fallito"); }
    finally { setBatchSending(false); }
  };
  const deleteBatch = async (b, e) => {
    if (e) e.stopPropagation();
    if (!window.confirm("Eliminare questa campagna e tutte le sue bozze?")) return;
    try {
      await api.delete(`/campaign-batches/${b._id}`);
      toast.success("Campagna eliminata");
      if (activeBatch?._id === b._id) setActiveBatch(null);
      loadBatches();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
  };

  // --- derived ---
  const waDigitsOf = (l) => (l?.wa_number ? l.wa_number.replace(/\D/g, "") : leadWaDigits(l));
  const kindOf = (l) => l?.phone_kind || phoneKind(l?.phone);
  const hasWaOf = (l) => !!(l?.wa_number) || !!leadWaDigits(l);
  const matchesChannel = (l) => {
    if (channelView === "all") return true;
    if (channelView === "email") return isValidEmail(l?.email);
    if (channelView === "whatsapp") return hasWaOf(l);
    if (channelView === "landline") return kindOf(l) === "landline" && !hasWaOf(l);
    return true;
  };
  const visibleLeads = sortRows(leads.filter((l) => {
    if ((Number(l?.fit_score) || 0) < Number(minScore)) return false;
    if (filterNation !== "tutte" && (l?.nation || "Italia") !== filterNation) return false;
    if (filterCity.trim() && !String(l?.city || "").toLowerCase().includes(filterCity.trim().toLowerCase())) return false;
    if (filterStatus !== "tutti" && (l?.status || "nuovo") !== filterStatus) return false;
    if (!matchesChannel(l)) return false;
    return true;
  }), sortBy, sortOptions, sortCatalog);
  const mappedLeads = visibleLeads.filter((l) => l?.location?.lat != null && l?.location?.lng != null);
  const selectedCount = visibleLeads.filter((l) => selected[l._id]).length;
  // Destinatari reali della campagna (su TUTTO l'archivio, non solo i visibili:
  // così l'invio dai risultati di ricerca funziona anche con filtri archivio attivi)
  const bulkRecipients = leads.filter((l) => selected[l._id]);
  const bulkEligible = bulkChannel === "whatsapp"
    ? bulkRecipients.filter((l) => hasWaOf(l))
    : bulkRecipients.filter((l) => isValidEmail(l?.email));
  const bulkSkippedCount = bulkRecipients.length - bulkEligible.length;
  const extraWaCount = bulkChannel === "whatsapp" ? extraWaRecipients.length : 0;
  const bulkTotalCount = bulkEligible.length + extraWaCount;

  const coverage = (() => {
    const src = searchResults.length > 0 ? searchResults : leads;
    const total = src.length;
    let email = 0, wa = 0, fisso = 0;
    src.forEach((l) => {
      if (isValidEmail(l?.email)) email++;
      const hasWa = hasWaOf(l);
      if (hasWa) wa++;
      if (!hasWa && kindOf(l) === "landline") fisso++;
    });
    const pct = (n) => (total ? Math.round((n / total) * 100) : 0);
    return { total, email, wa, fisso, pct, isSearch: searchResults.length > 0 };
  })();
  const visibleResults = searchResults.filter(matchesChannel).filter((r) => qualFilter === "all" || (r.qualification || "") === qualFilter)
    .slice().sort((a, b) => qualRank(a) - qualRank(b));
  const allSelected = visibleLeads.length > 0 && visibleLeads.every((l) => selected[l._id]);
  const draftBatches = batches.filter((b) => b.status !== "inviata");

  const toggleSelect = (id) => setSelected((s) => ({ ...s, [id]: !s[id] }));
  const clearSelection = () => setSelected({});
  const openBulkConfirm = () => {
    if (bulkChannel === "email" && !bulkBody.trim()) { toast.error("Scrivi il testo del messaggio"); return; }
    if (bulkTotalCount === 0) {
      toast.error(bulkChannel === "whatsapp" ? "Nessun destinatario con numero WhatsApp valido" : "Nessun destinatario con email valida");
      return;
    }
    setBulkConfirmOpen(true);
  };
  const addExtraRecipient = () => {
    const norm = normalizePhoneE164(newRecip.phone, "Italia");
    if (!norm) { toast.error("Numero non valido: inserisci un telefono valido (es. +39 348 1234567)"); return; }
    if (extraWaRecipients.some((r) => r.phone === norm)) { toast.error("Numero già aggiunto"); return; }
    setExtraWaRecipients((arr) => [...arr, { name: (newRecip.name || "").trim() || "Rivenditore", city: (newRecip.city || "").trim(), phone: norm, nation: "Italia" }]);
    setNewRecip({ name: "", city: "", phone: "" });
    toast.success(`Destinatario aggiunto: ${norm}`);
  };
  const removeExtraRecipient = (phone) => setExtraWaRecipients((arr) => arr.filter((r) => r.phone !== phone));
  const toggleSelectAll = () => {
    if (allSelected) { setSelected({}); return; }
    const next = {}; visibleLeads.forEach((l) => { next[l._id] = true; }); setSelected(next);
  };
  const selectByStatus = (status) => {
    const next = { ...selected };
    visibleLeads.filter((l) => (l?.status || "nuovo") === status).forEach((l) => { next[l._id] = true; });
    setSelected(next);
    toast.success(`Selezionati i rivenditori con stato "${status}"`);
  };
  const selectNotContacted = () => {
    const next = { ...selected };
    let n = 0;
    visibleLeads.filter((l) => daysSince(l?.last_contacted_at) >= Number(notContactedDays)).forEach((l) => { next[l._id] = true; n++; });
    setSelected(next);
    toast.success(`${n} rivenditori non contattati negli ultimi ${notContactedDays} giorni`);
  };
  const selectWithEmail = () => {
    const next = { ...selected }; let n = 0;
    visibleLeads.filter((l) => isValidEmail(l?.email)).forEach((l) => { next[l._id] = true; n++; });
    setSelected(next);
    toast.success(`${n} rivenditori con email valida selezionati`);
  };
  const selectWithWhatsapp = () => {
    const next = { ...selected }; let n = 0;
    visibleLeads.filter((l) => !!leadWaDigits(l)).forEach((l) => { next[l._id] = true; n++; });
    setSelected(next);
    toast.success(`${n} rivenditori con WhatsApp/cellulare selezionati`);
  };

  const mapSrc = (lead) => {
    if (!lead) return "";
    let q = "";
    if (lead.location?.lat != null && lead.location?.lng != null) q = `${lead.location.lat},${lead.location.lng}`;
    else q = lead.address || [lead.name, lead.city, lead.region].filter(Boolean).join(", ");
    return `https://www.google.com/maps?q=${encodeURIComponent(q)}&z=14&output=embed`;
  };

  return (
    <div>
      <PageHeader
        eyebrow="Automazione commerciale"
        title="AI Sales Agent"
        subtitle="Ricerca rivenditori premium di bici, moto e sport e prepara campagne di contatto iper-personalizzate."
      />

      <div className="mb-8">
        <AutopilotQueue module="rivenditori" onSaved={{ reload: loadLeads, navigate: showImported }} />
      </div>

      <ContactNowBanner items={justArchived} label="rivenditori" onContact={contactNext} onDismiss={() => setJustArchived([])} />

      {/* ===== Manual contact addition dialog ===== */}
      <ImportContactsDialog open={importOpen} onOpenChange={setImportOpen} section="leads" onDone={() => { loadLeads(); loadBatches(); }} onViewAll={showImported} />
      <Dialog open={manualOpen} onOpenChange={setManualOpen}>
        <DialogContent className="max-h-[88vh] max-w-lg overflow-y-auto border-border bg-brema-card" data-testid="manual-lead-dialog">
          <DialogHeader>
            <DialogTitle className="text-foreground">Aggiungi rivenditore manuale</DialogTitle>
            <DialogDescription>Inserisci un contatto che già conosci. Solo il nome è obbligatorio; potrai poi generare e inviare l'email.</DialogDescription>
          </DialogHeader>
          <div className="mb-3 rounded-lg border border-primary/30 bg-primary/5 p-3">
            <Label className="text-xs font-semibold text-primary">✨ Importazione rapida per nome</Label>
            <p className="mb-2 mt-0.5 text-xs text-muted-foreground">Digita il nome e l'AI cerca sul web i dati (sito, email, telefono, città). Poi controlla e salva.</p>
            <div className="flex gap-2">
              <Input data-testid="lead-lookup-input" value={lookupName} onChange={(e) => setLookupName(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); runLeadLookup(); } }}
                placeholder="es. All4cycling, Cicli Rossi Milano…" />
              <Button type="button" onClick={runLeadLookup} disabled={lookingUp} data-testid="lead-lookup-btn"
                className="shrink-0 rounded-full bg-primary font-semibold text-primary-foreground hover:bg-primary/90">
                {lookingUp ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Sparkles className="mr-1.5 h-4 w-4" /> Cerca con AI</>}
              </Button>
            </div>
          </div>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>Nome / Ragione sociale *</Label>
              <Input data-testid="manual-lead-name" value={manualLead.name} onChange={(e) => setML({ name: e.target.value })} placeholder="es. CicloSport Milano" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Email</Label>
                <Input data-testid="manual-lead-email" type="email" value={manualLead.email} onChange={(e) => setML({ email: e.target.value })} placeholder="info@negozio.it" />
              </div>
              <div className="space-y-1.5">
                <Label>Telefono</Label>
                <Input data-testid="manual-lead-phone" value={manualLead.phone} onChange={(e) => setML({ phone: e.target.value })} />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="flex items-center gap-1.5"><MessageCircle className="h-3.5 w-3.5 text-[#25D366]" /> Numero WhatsApp (opzionale)</Label>
              <Input data-testid="manual-lead-whatsapp" value={manualLead.whatsapp}
                onChange={(e) => setML({ whatsapp: e.target.value })}
                onBlur={() => { const n = normalizePhoneE164(manualLead.whatsapp, manualLead.nation); if (n) setML({ whatsapp: n }); }}
                placeholder="+39 333 1234567 — se diverso dal telefono fisso" />
              <p className="text-[11px] text-muted-foreground">Formattazione automatica in E.164 (es. +393481234567). Il numero WhatsApp deve essere un cellulare.</p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Categoria</Label>
                <Select value={manualLead.category} onValueChange={(v) => setML({ category: v })}>
                  <SelectTrigger data-testid="manual-lead-category"><SelectValue /></SelectTrigger>
                  <SelectContent>{categories.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Nazione</Label>
                <Select value={manualLead.nation} onValueChange={(v) => setML({ nation: v })}>
                  <SelectTrigger data-testid="manual-lead-nation"><SelectValue /></SelectTrigger>
                  <SelectContent>{NATIONS.map((n) => <SelectItem key={n} value={n}>{n}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Città</Label>
                <Input data-testid="manual-lead-city" value={manualLead.city} onChange={(e) => setML({ city: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label>Regione / Zona</Label>
                <Input data-testid="manual-lead-region" value={manualLead.region} onChange={(e) => setML({ region: e.target.value })} placeholder="es. Lombardia" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Sito web</Label>
                <Input data-testid="manual-lead-website" value={manualLead.website} onChange={(e) => setML({ website: e.target.value })} placeholder="www.negozio.it" />
              </div>
              <div className="space-y-1.5">
                <Label>Instagram</Label>
                <Input data-testid="manual-lead-instagram" value={manualLead.instagram} onChange={(e) => setML({ instagram: e.target.value })} placeholder="@handle" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Note</Label>
              <Textarea data-testid="manual-lead-notes" value={manualLead.notes} onChange={(e) => setML({ notes: e.target.value })} className="min-h-[70px]" placeholder="Info utili sul contatto…" />
            </div>
            <Button onClick={submitManualLead} disabled={manualSaving} data-testid="manual-lead-submit"
              className="w-full rounded-full bg-primary font-semibold text-primary-foreground hover:bg-primary/90">
              {manualSaving ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Salvataggio…</> : <><Save className="mr-2 h-4 w-4" /> Salva rivenditore</>}
            </Button>
          </div>
        </DialogContent>
      </Dialog>


      {/* ===== BOX 1: Ricerca autonoma (nuovi rivenditori) ===== */}
      <div className="mb-6 rounded-2xl border border-border bg-brema-card p-6">
        <div className="mb-4 flex flex-wrap items-center gap-x-2 gap-y-1 text-foreground">
          <Radar className="h-5 w-5 text-brand" />
          <h3 className="font-semibold">Ricerca autonoma negozi</h3>
          <span className="hidden text-xs font-normal text-muted-foreground sm:inline">· trova e genera nuovi rivenditori da internet</span>
          {category === "bici" && (
            <Button variant="secondary" size="sm" data-testid="manage-brands-btn" onClick={openBrands}
              className="ml-auto h-8 rounded-full border border-border bg-transparent text-xs text-muted-foreground hover:bg-brema-hover hover:text-foreground">
              <BadgeCheck className="mr-1.5 h-3.5 w-3.5" /> Marchi di riferimento
            </Button>
          )}
          <Button variant="secondary" size="sm" data-testid="reset-search-btn" onClick={resetSearch}
            className={`h-8 rounded-full border border-border bg-transparent text-xs text-muted-foreground hover:bg-brema-hover hover:text-foreground ${category === "bici" ? "" : "ml-auto"}`}>
            <RotateCcw className="mr-1.5 h-3.5 w-3.5" /> Nuova Ricerca
          </Button>
        </div>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-7 lg:items-end">
          <div className="space-y-1.5">
            <Label>Nazione</Label>
            <Select value={nation} onValueChange={(v) => { setNation(v); setProvince("tutte"); }}>
              <SelectTrigger data-testid="nation-select"><SelectValue /></SelectTrigger>
              <SelectContent className="max-h-[300px]">
                {NATIONS.map((n) => <SelectItem key={n} value={n}>{n}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {isItaly ? (
            <>
              <div className="space-y-1.5">
                <Label>Regione</Label>
                <Select value={region} onValueChange={(v) => { setRegion(v); setProvince("tutte"); }}>
                  <SelectTrigger data-testid="region-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="max-h-[300px]">
                    <SelectItem value="tutte">Tutte le regioni</SelectItem>
                    {REGIONS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Provincia</Label>
                <Select value={province} onValueChange={setProvince}>
                  <SelectTrigger data-testid="province-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="max-h-[300px]">
                    <SelectItem value="tutte">Tutte le province</SelectItem>
                    {(PROVINCES_BY_REGION[region] || []).map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </>
          ) : (
            <>
              <div className="space-y-1.5">
                <Label>Città / Hub</Label>
                <Input value={city} data-testid="city-input" placeholder="es. Paris, München…"
                  onChange={(e) => setCity(e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label>Raggio (km)</Label>
                <Input type="number" min={1} max={200} value={radius} data-testid="radius-input"
                  onChange={(e) => setRadius(e.target.value)} />
              </div>
            </>
          )}
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <Label>Categoria</Label>
              <Button type="button" variant="ghost" size="sm" className="h-6 px-2 text-xs text-muted-foreground hover:text-foreground" onClick={() => setCatMgr(true)} data-testid="manage-sales-category-btn"><Settings2 className="mr-1 h-3.5 w-3.5" /> Gestisci</Button>
            </div>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger data-testid="category-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {categories.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <FinderOptionsDialog group="sales_category" title="Categoria — AI Sales Agent" withQuery open={catMgr} onOpenChange={setCatMgr} onChanged={loadCategories} />
          </div>
          <div className="space-y-1.5">
            <Label>Qualità / Match</Label>
            <Select value={minScoreSearch} onValueChange={setMinScoreSearch}>
              <SelectTrigger data-testid="search-quality-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {QUALITY_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Contatto</Label>
            <Select value={contactFilter} onValueChange={setContactFilter}>
              <SelectTrigger data-testid="contact-filter-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="any">Tutti i contatti</SelectItem>
                <SelectItem value="email">Solo con Email</SelectItem>
                <SelectItem value="whatsapp">Solo con WhatsApp/Cell.</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Quanti contattabili</Label>
            <Input type="number" min={1} max={30} value={count} data-testid="count-input"
              onChange={(e) => setCount(e.target.value)} />
          </div>
          <Button onClick={discover} disabled={discovering} data-testid="discover-btn"
            className="flex w-full items-center justify-center gap-2 rounded-full bg-primary font-semibold leading-none text-primary-foreground hover:bg-primary/90">
            {discovering ? <><Loader2 className="h-4 w-4 animate-spin" /> Ricerca…</> : <><Sparkles className="h-4 w-4" /> {searchResults.length > 0 ? "Trova altri" : "Avvia ricerca AI"}</>}
          </Button>
          <Button onClick={() => setDiscoveryOpen(true)} variant="outline" data-testid="ai-discovery-btn"
            className="flex w-full items-center justify-center gap-2 rounded-full border-primary/40 font-semibold leading-none text-primary hover:bg-primary/10">
            <Target className="h-4 w-4" /> AI Discovery Search
          </Button>
        </div>
        <AIDiscoveryDialog open={discoveryOpen} onOpenChange={setDiscoveryOpen}
          title="AI Discovery Search · Rivenditori"
          searchEndpoint="/leads/discovery-search" archiveEndpoint="/leads/discovery-archive"
          searchBody={{ nation, region: region === "tutte" ? "" : region, province: province === "tutte" ? "" : province, category, count: Number(count) || 8, min_score: Number(minScoreSearch) || 0, contact_filter: contactFilter }}
          getKey={(c, i) => c.dedup_key || c.temp_id || String(i)}
          getWhy={(c) => c.why}
          renderSummary={(data) => data.report ? <DiscoveryReportPanel report={data.report} compact /> : null}
          renderCandidate={(c) => (
            <div>
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-semibold">{c.name}</span>
                <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-semibold text-primary">Fit {c.fit_score}</span>
                <QualBadge lead={c} testid="discovery-qual-badge" />
              </div>
              <div className="text-xs text-muted-foreground">
                {[c.city, c.region].filter(Boolean).join(", ")}
                {c.website && (<>{" · "}
                  <a href={c.website.startsWith("http") ? c.website : `https://${c.website}`}
                    target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}
                    data-testid="discovery-website-link" className="text-primary hover:underline">
                    {c.website.replace(/^https?:\/\//, "")}
                  </a>
                </>)}
              </div>
            </div>
          )}
          onArchived={(data) => { loadLeads(); setJustArchived(data?.leads || []); }} />
        {discovering && (
          <div className="mt-3 rounded-lg border border-primary/30 bg-primary/5 px-3 py-2" data-testid="discover-progress">
            <div className="flex items-center gap-2 text-xs text-brand">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              {discoverProgress?.engine === "brema-qualifier" ? (
                <span data-testid="discover-progress-qualifier">
                  {discoverProgress.phase === "discovery" && <>Discovery ampia · query {discoverProgress.queries_done ?? 0}/{discoverProgress.queries_total ?? "?"}{discoverProgress.current_area ? ` · ${discoverProgress.current_area}` : ""} · {discoverProgress.candidates ?? 0} candidati</>}
                  {discoverProgress.phase === "analyzing" && <>Analisi siti + qualificazione Brema (KB, marchi, esclusioni) · {discoverProgress.analyzed ?? 0}/{discoverProgress.max_analyzed ?? 50} analizzati</>}
                  {(discoverProgress.phase === "qualifying" || discoverProgress.phase === "done") && <>Qualificati <b>{discoverProgress.qualified ?? 0}/{discoverProgress.target ?? count}</b> · {discoverProgress.to_verify ?? 0} da verificare · {discoverProgress.excluded ?? 0} esclusi · {discoverProgress.analyzed ?? 0} analizzati</>}
                </span>
              ) : discoverProgress?.phase === "searching" ? (
                <span>
                  Ricerca web dei rivenditori premium
                  {discoverProgress.current_area ? ` in ${discoverProgress.current_area}` : ""}…
                </span>
              ) : discoverProgress?.phase === "compiling" ? (
                <span>
                  L'AI sta selezionando i rivenditori in target
                  {discoverProgress.candidates ? ` (${discoverProgress.candidates} fonti web)` : ""}…
                </span>
              ) : discoverProgress?.phase === "scanning" ? (
                <span>
                  Scansione a ciclo continuo · provincia {(discoverProgress.areas_done ?? 0) + 1}/{discoverProgress.areas_total}
                  {discoverProgress.current_area ? ` · ${discoverProgress.current_area}` : ""}
                  {" — trovati "}<b>{discoverProgress.found ?? 0}/{discoverProgress.target ?? count}</b> contattabili
                </span>
              ) : discoverProgress?.phase === "enriching" ? (
                <span>
                  Recupero contatti (email/telefono) in sottofondo
                  {discoverProgress.candidates ? ` · ${discoverProgress.candidates} rivenditori` : ""}…
                </span>
              ) : (
                <span>Avvio ricerca…</span>
              )}
            </div>
          </div>
        )}
        {contactFilter !== "any" && !discovering && (
          <p className="mt-2 text-[11px] text-muted-foreground" data-testid="contact-filter-hint">
            Con il filtro {contactFilter === "email" ? "Email" : "WhatsApp/Cellulare"} attivo, mostriamo solo i rivenditori
            per cui l'enrichment trova un {contactFilter === "email" ? "indirizzo email" : "numero cellulare/WhatsApp"} valido.
          </p>
        )}
        {discoverReport && !discovering && (
          <div className="mt-3"><DiscoveryReportPanel report={discoverReport} onClose={() => setDiscoverReport(null)} /></div>
        )}
        <p className="mt-3 text-xs text-muted-foreground">
          {(integrations?.brave || integrations?.google_cse || integrations?.google_places) ? (
            <span className="flex items-center gap-1.5 text-positive" data-testid="integration-status-real">
              <Check className="h-3.5 w-3.5" />
              Motore LLM-First attivo · Ricerca semantica AI + Web Search{integrations?.hunter ? " + enrichment Hunter.io (email)" : " · Hunter.io non attivo (email non arricchite)"}
            </span>
          ) : (
            <span data-testid="integration-status-demo">
              Modalità dimostrativa: risultati generati dall'AI (dati simulati).
            </span>
          )}
        </p>
      </div>

      {/* ===== Risultati ricerca (staging temporaneo, non salvato) ===== */}
      {searchResults.length > 0 && (
        <div className="mb-6 rounded-2xl border-2 border-dashed border-primary/40 bg-brema-card p-6" data-testid="search-results">
          <div className="mb-4 flex flex-wrap items-center gap-2">
            <Sparkles className="h-5 w-5 text-brand" />
            <h3 className="font-semibold text-foreground">Risultati ricerca</h3>
            <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-medium text-brand">anteprima · non ancora salvati</span>
            <span className="text-xs text-muted-foreground">· {searchResults.length} in lista (le ricerche si accumulano)</span>
            {searchResults.some((r) => r.qualification) && (
              <Select value={qualFilter} onValueChange={setQualFilter}>
                <SelectTrigger className="h-8 w-[190px] rounded-full text-xs" data-testid="results-qual-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutte le qualifiche</SelectItem>
                  <SelectItem value="qualificato">Qualificati ≥70</SelectItem>
                  <SelectItem value="da_verificare">Da verificare</SelectItem>
                  <SelectItem value="brand">Brand 100% (legacy)</SelectItem>
                  <SelectItem value="apparel">Apparel (legacy)</SelectItem>
                </SelectContent>
              </Select>
            )}
            <div className="ml-auto flex flex-wrap items-center gap-2">
              <span className="text-xs text-muted-foreground" data-testid="results-selected-count">
                {searchResults.filter((r) => resultSel[r.temp_id]).length} selezionati
              </span>
              <Button size="sm" variant="secondary" data-testid="results-select-all-btn" onClick={toggleAllResults}
                className="h-8 rounded-full border border-border bg-transparent text-xs text-foreground hover:bg-brema-hover">
                Seleziona tutti
              </Button>
              <div className="flex items-center overflow-hidden rounded-full border border-border" data-testid="results-channel-toggle">
                <button type="button" data-testid="results-channel-email"
                  onClick={() => setBulkChannel("email")}
                  className={`px-3 py-1.5 text-xs font-medium transition-colors ${bulkChannel === "email" ? "bg-selected text-selected-foreground" : "bg-transparent text-muted-foreground hover:bg-brema-hover"}`}>
                  <Mail className="mr-1 inline h-3.5 w-3.5" /> Email
                </button>
                <button type="button" data-testid="results-channel-whatsapp"
                  onClick={() => setBulkChannel("whatsapp")}
                  className={`px-3 py-1.5 text-xs font-medium transition-colors ${bulkChannel === "whatsapp" ? "bg-positive text-white" : "bg-transparent text-muted-foreground hover:bg-brema-hover"}`}>
                  <MessageCircle className="mr-1 inline h-3.5 w-3.5" /> WhatsApp
                </button>
              </div>
              <Button size="sm" variant="secondary" data-testid="save-all-results-btn"
                disabled={savingResults || searchResults.filter((r) => !r.already_saved).length === 0}
                onClick={saveAllResults}
                className="h-8 rounded-full border border-border bg-transparent text-xs font-medium text-foreground hover:bg-brema-hover disabled:opacity-40">
                <Save className="mr-1.5 h-3.5 w-3.5" /> Salva tutti ({searchResults.filter((r) => !r.already_saved).length})
              </Button>
              <Button size="sm" variant="secondary" data-testid="save-selected-results-btn"
                disabled={savingResults || searchResults.filter((r) => resultSel[r.temp_id]).length === 0}
                onClick={saveSelectedResults}
                className="h-8 rounded-full border border-border bg-transparent text-xs font-medium text-foreground hover:bg-brema-hover disabled:opacity-40">
                {savingResults ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Save className="mr-1.5 h-3.5 w-3.5" />}
                Salva selezionati ({searchResults.filter((r) => resultSel[r.temp_id]).length})
              </Button>
              <Button size="sm" data-testid="outreach-selected-results-btn"
                disabled={savingResults || searchResults.filter((r) => resultSel[r.temp_id]).length === 0}
                onClick={outreachSelectedResults}
                className="h-8 rounded-full bg-primary text-xs font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-40">
                <Send className="mr-1.5 h-3.5 w-3.5" /> Invia {bulkChannel === "whatsapp" ? "WhatsApp" : "email"} ai selezionati ({searchResults.filter((r) => resultSel[r.temp_id]).length})
              </Button>
              <Button size="sm" variant="secondary" data-testid="clear-results-btn" onClick={() => { setSearchResults([]); setResultSel({}); }}
                className="h-8 rounded-full border border-border bg-transparent text-xs text-muted-foreground hover:bg-brema-hover">
                <X className="mr-1.5 h-3.5 w-3.5" /> Nuova lista
              </Button>
            </div>
          </div>
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {visibleResults.map((r, i) => (
              <div key={r.temp_id || i} data-testid="result-card" onClick={() => setDetailLead(r)}
                className={`flex cursor-pointer flex-col rounded-xl border p-4 transition-colors hover:border-primary/50 ${r.already_saved ? "border-positive/40 bg-positive/5" : resultSel[r.temp_id] ? "border-primary ring-1 ring-primary/30 bg-primary/5" : "border-border bg-brema-bg"}`}>
                <div className="mb-2 flex items-start justify-between gap-2">
                  <div className="flex items-start gap-2">
                    <div onClick={(e) => e.stopPropagation()}>
                      <Checkbox checked={!!resultSel[r.temp_id]} disabled={r.already_saved}
                        onCheckedChange={() => toggleResultSel(r.temp_id)} data-testid="result-select-checkbox"
                        className="mt-1 border-border data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground" />
                    </div>
                    <div>
                      <h4 className="font-semibold leading-tight text-foreground">{r.name}</h4>
                      <p className="flex items-center gap-1 text-xs text-muted-foreground">
                        <MapPin className="h-3 w-3" /> {r.city || "N/D"}, {r.region || "N/D"}
                        {typeof r.rating === "number" && <span className="ml-1 text-[10px] text-brand">★ {r.rating}{r.reviews ? ` (${r.reviews})` : ""}</span>}
                      </p>
                      <QualBadge lead={r} />
                    </div>
                  </div>
                  <ScoreBadge score={r.fit_score} />
                </div>
                {r.description && <p className="mb-2 line-clamp-2 text-[13px] leading-snug text-foreground">{r.description}</p>}
                <div className="mb-3 space-y-1 text-[13px]">
                  {r.email ? (
                    <span className="flex items-center gap-1.5 text-foreground"><Mail className="h-3.5 w-3.5 shrink-0 text-brand" /> <span className="truncate">{r.email}</span></span>
                  ) : r.enriching ? (
                    <span className="flex items-center gap-1.5 text-xs text-brand" data-testid="result-enriching"><Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin" /> Recupero contatti…</span>
                  ) : (
                    <span className="flex items-center gap-1.5 text-xs text-muted-foreground"><Mail className="h-3.5 w-3.5 shrink-0" /> Email non disponibile</span>
                  )}
                  {(r.phone_e164 || r.phone) && (
                    <span className="flex items-center gap-1.5 text-foreground"><Phone className="h-3.5 w-3.5 shrink-0" /> {formatE164Display(r.phone_e164) || r.phone}
                      {kindOf(r) === "mobile" && <span className="rounded-full bg-[#25D366]/15 px-1.5 py-0.5 text-[9px] font-medium text-[#128C4A] dark:text-[#25D366]">Cell.</span>}
                    </span>
                  )}
                  {r.website && (
                    <a href={extUrl(r.website)} target="_blank" rel="noopener noreferrer" data-testid="result-website-link"
                      onClick={(e) => e.stopPropagation()}
                      className="flex items-center gap-1.5 text-foreground hover:text-brand hover:underline">
                      <Globe className="h-3.5 w-3.5 shrink-0 text-brand" /> <span className="truncate">{String(r.website).replace(/^https?:\/\//, "")}</span>
                    </a>
                  )}
                </div>
                <div className="mt-auto flex flex-wrap items-center gap-2" onClick={(e) => e.stopPropagation()}>
                  {r.already_saved ? (
                    <span data-testid="result-already-saved" className="inline-flex items-center gap-1.5 rounded-full bg-positive/10 px-2.5 py-1.5 text-xs font-medium text-positive">
                      <Check className="h-3.5 w-3.5" /> Già in archivio
                    </span>
                  ) : (
                    <Button size="sm" data-testid="save-one-result-btn" disabled={savingResults} onClick={() => saveOneResult(r)}
                      className="flex-1 rounded-full bg-primary text-xs font-semibold text-primary-foreground hover:bg-primary/90">
                      <Plus className="mr-1.5 h-3.5 w-3.5" /> Salva
                    </Button>
                  )}
                  <Button size="sm" variant="secondary" data-testid="result-detail-btn" onClick={() => setDetailLead(r)}
                    className="rounded-full border border-border bg-transparent text-xs text-foreground hover:bg-brema-hover">
                    <Eye className="mr-1 h-3.5 w-3.5" /> Dettaglio
                  </Button>
                  <Button size="sm" data-testid="result-outreach-btn" onClick={() => outreachFromResult(r)}
                    className="rounded-full bg-[#25D366]/10 text-xs font-semibold text-[#128C4A] hover:bg-[#25D366]/20 dark:text-[#25D366]">
                    <Sparkles className="mr-1 h-3.5 w-3.5" /> Outreach
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ===== Copertura Canali (sopra i tab) ===== */}
      {(searchResults.length > 0 || leads.length > 0) && (
        <div className="mb-6 rounded-2xl border border-border bg-brema-card p-5" data-testid="channel-coverage">
          <div className="mb-4 flex flex-wrap items-center gap-2 text-foreground">
            <Radar className="h-4 w-4 text-brand" />
            <h3 className="text-sm font-semibold">Copertura Canali</h3>
            <span className="text-xs text-muted-foreground" data-testid="coverage-scope">
              {coverage.isSearch ? `sui ${coverage.total} risultati della ricerca` : `su ${coverage.total} rivenditori in archivio`}
            </span>
            {coverage.isSearch && <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-medium text-brand">ricerca attiva</span>}
            {channelView !== "all" && (
              <button type="button" data-testid="coverage-clear" onClick={() => setChannelView("all")}
                className="ml-auto inline-flex items-center gap-1 rounded-full border border-border px-2.5 py-1 text-xs text-muted-foreground hover:bg-brema-hover">
                <X className="h-3 w-3" /> Rimuovi filtro
              </button>
            )}
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <button type="button" onClick={() => { setChannelView((v) => (v === "email" ? "all" : "email")); setTab("rivenditori"); }}
              className={`rounded-xl border p-4 text-left transition-colors ${channelView === "email" ? "border-sky-500 ring-2 ring-sky-500/30 bg-sky-500/5" : "border-border bg-brema-bg hover:border-sky-500/50"}`} data-testid="coverage-email">
              <div className="mb-2 flex items-center gap-2 text-sm font-medium text-foreground"><Mail className="h-4 w-4 text-sky-500" /> Email valida</div>
              <div className="flex items-baseline gap-2"><span className="text-2xl font-bold text-foreground">{coverage.email}</span><span className="text-sm text-muted-foreground">{coverage.pct(coverage.email)}%</span></div>
              <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-muted"><div className="h-full rounded-full bg-sky-500" style={{ width: `${coverage.pct(coverage.email)}%` }} /></div>
            </button>
            <button type="button" onClick={() => { setChannelView((v) => (v === "whatsapp" ? "all" : "whatsapp")); setTab("rivenditori"); }}
              className={`rounded-xl border p-4 text-left transition-colors ${channelView === "whatsapp" ? "border-[#25D366] ring-2 ring-[#25D366]/30 bg-[#25D366]/5" : "border-border bg-brema-bg hover:border-[#25D366]/50"}`} data-testid="coverage-whatsapp">
              <div className="mb-2 flex items-center gap-2 text-sm font-medium text-foreground"><MessageCircle className="h-4 w-4 text-[#25D366]" /> WhatsApp / Cellulare</div>
              <div className="flex items-baseline gap-2"><span className="text-2xl font-bold text-foreground">{coverage.wa}</span><span className="text-sm text-muted-foreground">{coverage.pct(coverage.wa)}%</span></div>
              <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-muted"><div className="h-full rounded-full bg-[#25D366]" style={{ width: `${coverage.pct(coverage.wa)}%` }} /></div>
            </button>
            <button type="button" onClick={() => { setChannelView((v) => (v === "landline" ? "all" : "landline")); setTab("rivenditori"); }}
              className={`rounded-xl border p-4 text-left transition-colors ${channelView === "landline" ? "border-amber-500 ring-2 ring-amber-500/30 bg-amber-500/5" : "border-border bg-brema-bg hover:border-amber-500/50"}`} data-testid="coverage-landline">
              <div className="mb-2 flex items-center gap-2 text-sm font-medium text-foreground"><Phone className="h-4 w-4 text-amber-500" /> Solo Fisso</div>
              <div className="flex items-baseline gap-2"><span className="text-2xl font-bold text-foreground">{coverage.fisso}</span><span className="text-sm text-muted-foreground">{coverage.pct(coverage.fisso)}%</span></div>
              <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-muted"><div className="h-full rounded-full bg-amber-500" style={{ width: `${coverage.pct(coverage.fisso)}%` }} /></div>
            </button>
          </div>
        </div>
      )}

      <Tabs value={tab} onValueChange={(v) => { setTab(v); if (v === "esclusi") loadExcluded(); }}>
        <TabsList className="bg-brema-card">
          <TabsTrigger value="rivenditori" data-testid="tab-rivenditori"><Users className="mr-1.5 h-4 w-4" /> Rivenditori ({visibleLeads.length})</TabsTrigger>
          <TabsTrigger value="campagne" data-testid="tab-campagne"><Archive className="mr-1.5 h-4 w-4" /> Bozze ({draftBatches.length})</TabsTrigger>
          <TabsTrigger value="storico" data-testid="tab-storico"><Inbox className="mr-1.5 h-4 w-4" /> Storico Invii ({history.length})</TabsTrigger>
          <TabsTrigger value="esclusi" data-testid="tab-esclusi"><X className="mr-1.5 h-4 w-4" /> Esclusi ({excluded.length})</TabsTrigger>
        </TabsList>

        {/* ================= TAB RIVENDITORI ================= */}
        <TabsContent value="rivenditori">
          {/* BOX 2: Archivio & Gestione */}
          <div className="mb-4 rounded-2xl border border-border bg-brema-card p-5">
            <div className="mb-3 flex items-center gap-2 text-foreground">
              <Archive className="h-4 w-4 text-brand" />
              <h3 className="text-sm font-semibold">Archivio & Gestione Campagne Rivenditori</h3>
            </div>
            <div className="flex flex-wrap items-end gap-3">
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">Nazione</Label>
                <Select value={filterNation} onValueChange={setFilterNation}>
                  <SelectTrigger className="h-9 w-40" data-testid="filter-nation-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="max-h-[300px]">
                    <SelectItem value="tutte">Tutte le nazioni</SelectItem>
                    {NATIONS.map((n) => <SelectItem key={n} value={n}>{n}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">Città / Hub</Label>
                <Input value={filterCity} data-testid="filter-city-input" placeholder="Filtra città…"
                  className="h-9 w-40" onChange={(e) => setFilterCity(e.target.value)} />
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">Qualità</Label>
                <Select value={minScore} onValueChange={setMinScore}>
                  <SelectTrigger className="h-9 w-40" data-testid="quality-filter-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {QUALITY_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">Stato</Label>
                <Select value={filterStatus} onValueChange={setFilterStatus}>
                  <SelectTrigger className="h-9 w-40" data-testid="filter-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="tutti">Tutti gli stati</SelectItem>
                    {STATUSES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">Contatti</Label>
                <Select value={channelView} onValueChange={setChannelView}>
                  <SelectTrigger className="h-9 w-40" data-testid="filter-channel-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tutti i canali</SelectItem>
                    <SelectItem value="email">Solo con Email</SelectItem>
                    <SelectItem value="whatsapp">Solo con WhatsApp</SelectItem>
                    <SelectItem value="landline">Solo fisso</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <SortSelect options={sortOptions} value={sortBy} onChange={setSortBy} testid="sort-by-select" label="Ordina per" className="h-9 w-56 text-xs" />
              <div className="flex w-full flex-wrap items-center gap-2 sm:ml-auto sm:w-auto">
                <Button size="sm" data-testid="add-manual-lead-btn" onClick={() => { setManualLead(emptyManualLead); setLookupName(""); setManualOpen(true); }}
                  className="h-9 rounded-full bg-primary text-xs font-semibold text-primary-foreground hover:bg-primary/90">
                  <Plus className="mr-1.5 h-3.5 w-3.5" /> Aggiungi rivenditore
                </Button>
                <div className="flex items-center gap-1 rounded-full border border-border p-0.5">
                  <Button size="sm" variant="ghost" data-testid="view-list-btn" onClick={() => setViewMode("list")}
                    className={`h-7 rounded-full px-3 text-xs ${viewMode === "list" ? "bg-selected text-selected-foreground hover:bg-selected/90 hover:text-selected-foreground" : "text-foreground hover:bg-brema-hover"}`}>
                    <List className="mr-1 h-3.5 w-3.5" /> Lista
                  </Button>
                  <Button size="sm" variant="ghost" data-testid="view-map-btn" onClick={() => setViewMode("map")}
                    className={`h-7 rounded-full px-3 text-xs ${viewMode === "map" ? "bg-selected text-selected-foreground hover:bg-selected/90 hover:text-selected-foreground" : "text-foreground hover:bg-brema-hover"}`}>
                    <MapIcon className="mr-1 h-3.5 w-3.5" /> Mappa
                  </Button>
                </div>
                <Button variant="secondary" size="sm" data-testid="import-leads-btn" onClick={() => setImportOpen(true)}
                  className="h-9 rounded-full border border-border bg-transparent text-xs text-foreground hover:bg-brema-hover">
                  <FileSpreadsheet className="mr-1.5 h-3.5 w-3.5" /> Importa CSV/Excel
                </Button>
                {leads.length > 0 && (
                  <>
                    <Button variant="secondary" size="sm" data-testid="export-leads-btn" onClick={exportCsv}
                      className="h-9 rounded-full border border-border bg-transparent text-xs text-foreground hover:bg-brema-hover">
                      <Download className="mr-1.5 h-3.5 w-3.5" /> Esporta CSV
                    </Button>
                    <Button variant="secondary" size="sm" data-testid="reset-filters-btn" onClick={resetFilters}
                      className="h-9 rounded-full border border-border bg-transparent text-xs text-foreground hover:bg-brema-hover">
                      <RotateCcw className="mr-1.5 h-3.5 w-3.5" /> Azzera filtri
                    </Button>
                    <Button variant="secondary" size="sm" data-testid="leads-dedup-btn" onClick={dedupLeads} disabled={dedupingLeads}
                      className="h-9 rounded-full border border-border bg-transparent text-xs text-foreground hover:bg-brema-hover">
                      {dedupingLeads ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Layers className="mr-1.5 h-3.5 w-3.5" />} Rimuovi duplicati
                    </Button>
                    {selectedCount > 0 && (
                      <Button variant="danger" size="sm" data-testid="delete-selected-btn" onClick={() => { setDeleteSelConfirm(""); setDeleteSelOpen(true); }}
                        title={`Elimina ${selectedCount} rivenditori selezionati`}
                        className="h-9 rounded-full px-3 text-xs">
                        <Trash2 className="mr-1.5 h-3.5 w-3.5" /> Elimina ({selectedCount})
                      </Button>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Bulk selection toolbar */}
          {viewMode === "list" && visibleLeads.length > 0 && (
            <div className="mb-4 flex flex-wrap items-center gap-3 rounded-xl border border-border bg-brema-card p-3" data-testid="bulk-toolbar">
              <Button variant="secondary" size="sm" data-testid="select-all-btn" onClick={toggleSelectAll}
                className="rounded-full border border-border bg-transparent text-xs text-foreground hover:bg-brema-hover">
                {allSelected ? "Deseleziona tutti" : "Seleziona tutti"}
              </Button>
              <Select value="" onValueChange={selectByStatus}>
                <SelectTrigger className="h-8 w-44 text-xs" data-testid="select-by-status"><SelectValue placeholder="Seleziona per stato…" /></SelectTrigger>
                <SelectContent>
                  {STATUSES.map((s) => <SelectItem key={s} value={s} className="capitalize">Seleziona i "{s}"</SelectItem>)}
                </SelectContent>
              </Select>
              <div className="flex items-center gap-1.5">
                <span className="text-xs text-muted-foreground">Non contattati da</span>
                <Input type="number" min={0} value={notContactedDays} data-testid="not-contacted-days"
                  onChange={(e) => setNotContactedDays(e.target.value)} className="h-8 w-16 text-xs" />
                <span className="text-xs text-muted-foreground">gg</span>
                <Button variant="secondary" size="sm" data-testid="select-not-contacted-btn" onClick={selectNotContacted}
                  className="rounded-full border border-border bg-transparent text-xs text-foreground hover:bg-brema-hover">
                  <Clock className="mr-1 h-3.5 w-3.5" /> Seleziona
                </Button>
              </div>
              <Button variant="secondary" size="sm" data-testid="select-with-email-btn" onClick={selectWithEmail}
                className="rounded-full border border-border bg-transparent text-xs text-foreground hover:bg-brema-hover">
                <Mail className="mr-1 h-3.5 w-3.5" /> Solo con Email
              </Button>
              <Button variant="secondary" size="sm" data-testid="select-with-whatsapp-btn" onClick={selectWithWhatsapp}
                className="rounded-full border border-border bg-transparent text-xs text-foreground hover:bg-brema-hover">
                <MessageCircle className="mr-1 h-3.5 w-3.5 text-[#25D366]" /> Solo con WhatsApp
              </Button>
              <span className="text-sm text-foreground" data-testid="selected-counter">
                {selectedCount} {selectedCount === 1 ? "selezionato" : "selezionati"}
              </span>
              {selectedCount > 0 && (
                <Button variant="danger" size="sm" data-testid="deselect-all-btn" onClick={clearSelection}
                  className="rounded-full text-xs">
                  <X className="mr-1 h-3.5 w-3.5" /> Deseleziona tutti
                </Button>
              )}
              <div className="flex w-full flex-wrap items-center gap-2 sm:ml-auto sm:w-auto">
                <Select value={bulkChannel} onValueChange={setBulkChannel}>
                  <SelectTrigger className="h-9 w-40 text-xs" data-testid="bulk-channel-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="email">Canale: E-mail</SelectItem>
                    <SelectItem value="whatsapp">Canale: WhatsApp</SelectItem>
                  </SelectContent>
                </Select>
                <Button data-testid="bulk-outreach-btn" disabled={selectedCount === 0} onClick={openBulkComposer}
                  className={`rounded-full font-semibold disabled:opacity-40 ${bulkChannel === "whatsapp" ? "bg-[#25D366] text-white hover:bg-[#1FB855]" : "bg-primary text-primary-foreground hover:bg-primary/90"}`}>
                  {bulkChannel === "whatsapp"
                    ? (<><MessageCircle className="mr-2 h-4 w-4" /> Avvia Campagna WhatsApp</>)
                    : (<><Send className="mr-2 h-4 w-4" /> Avvia Campagna di Outreach</>)}
                </Button>
              </div>
            </div>
          )}

          {viewMode === "map" ? (
            <div className="rounded-xl border border-border bg-brema-card p-3" data-testid="leads-map-view">
              <div className="mb-2 flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground">
                <span>
                  <span className="font-medium text-foreground">{mappedLeads.length}</span> rivenditori con posizione su mappa
                  {mappedLeads.length < visibleLeads.length && (
                    <span> · {visibleLeads.length - mappedLeads.length} senza coordinate (non mostrati)</span>
                  )}
                </span>
                <div className="flex flex-wrap items-center gap-3">
                  <span className="flex items-center gap-1"><span className="inline-block h-2.5 w-2.5 rounded-full" style={{ background: markerColor(90) }} /> Match ≥ 85%</span>
                  <span className="flex items-center gap-1"><span className="inline-block h-2.5 w-2.5 rounded-full" style={{ background: markerColor(75) }} /> 70–84%</span>
                  <span className="flex items-center gap-1"><span className="inline-block h-2.5 w-2.5 rounded-full" style={{ background: markerColor(50) }} /> &lt; 70%</span>
                </div>
              </div>
              <div className="h-[560px] overflow-hidden rounded-lg border border-border" data-testid="leads-map">
                {mapReady && (
                  <MapContainer center={[48, 10]} zoom={4} style={{ height: "100%", width: "100%" }} scrollWheelZoom>
                    <TileLayer attribution="&copy; OpenStreetMap" url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                    <FitBounds points={mappedLeads.map((l) => [l.location.lat, l.location.lng])} />
                    {mappedLeads.map((l, i) => (
                      <CircleMarker key={l._id || i} center={[l.location.lat, l.location.lng]} radius={9}
                        pathOptions={{ color: "#131822", fillColor: markerColor(Number(l.fit_score) || 0), fillOpacity: 0.95, weight: 2 }}>
                        <Popup>
                          <div className="space-y-1 text-slate-800" data-testid="map-popup">
                            <p className="text-sm font-semibold">{l.name}</p>
                            <p className="text-xs text-muted-foreground">{l.city}{l.region ? `, ${l.region}` : ""}</p>
                            <p className="text-xs text-muted-foreground">Match {l.fit_score}%{typeof l.rating === "number" ? ` · ★ ${l.rating}` : ""}</p>
                            <button type="button" data-testid="map-popup-detail-btn" onClick={() => setDetailLead(l)}
                              className="mt-1 rounded bg-[#131822] px-2.5 py-1 text-xs font-medium text-white">Dettaglio</button>
                          </div>
                        </Popup>
                      </CircleMarker>
                    ))}
                  </MapContainer>
                )}
              </div>
              {mappedLeads.length === 0 && (
                <p className="mt-3 text-center text-sm text-muted-foreground">
                  Nessun rivenditore con coordinate per questo filtro. I rivenditori trovati con dati reali (Google Places) includono la posizione.
                </p>
              )}
            </div>
          ) : visibleLeads.length === 0 ? (
            <div className="rounded-xl border border-dashed border-border bg-brema-card/50 p-12 text-center text-muted-foreground">
              {leads.length === 0
                ? "Nessun rivenditore in archivio. Avvia una ricerca o importa un file CSV/Excel per popolarlo."
                : "Nessun rivenditore per i filtri selezionati."}
            </div>
          ) : (
            <ErrorBoundary>
              <div className="overflow-x-auto rounded-xl border border-border bg-brema-card" data-testid="leads-table-wrap">
                <table className="w-full text-sm" data-testid="leads-table">
                  <thead>
                    <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-muted-foreground">
                      <th className="w-10 px-3 py-3"></th>
                      <th className="px-4 py-3 font-medium">Negozio</th>
                      <th className="px-4 py-3 font-medium">Città</th>
                      <th className="px-4 py-3 font-medium">Contatti</th>
                      <th className="px-4 py-3 font-medium">Match</th>
                      <th className="px-4 py-3 font-medium">Stato</th>
                      <th className="px-4 py-3 text-right font-medium">Azioni</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleLeads.map((lead, i) => (
                      <tr key={lead?._id || i} data-testid="lead-row" onClick={() => setDetailLead(lead)}
                        className="cursor-pointer border-b border-border text-foreground last:border-0 hover:bg-muted">
                        <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}>
                          <Checkbox checked={!!selected[lead?._id]} onCheckedChange={() => toggleSelect(lead?._id)}
                            data-testid="lead-select-checkbox" className="border-border data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground" />
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-foreground">{lead?.name || "Rivenditore"}</span>
                            {lead?.notes && lead.notes.trim() && (
                              <StickyNote className="h-3.5 w-3.5 text-brema-yellow" data-testid="lead-note-icon" title="Ha note/accordi" />
                            )}
                            {lead?.source === "real" && (
                              <span data-testid="lead-source-badge" className="rounded-full bg-brema-navy px-1.5 py-0.5 text-[9px] font-medium text-white dark:bg-emerald-500/15 dark:text-emerald-400">Reale</span>
                            )}
                            {lead?.source === "simulated" && (
                              <span data-testid="lead-source-badge" className="rounded-full bg-muted px-1.5 py-0.5 text-[9px] text-muted-foreground">Demo</span>
                            )}
                          </div>
                          {typeof lead?.rating === "number" && (
                            <span className="text-[10px] text-brand">★ {lead.rating}{lead?.reviews ? ` (${lead.reviews})` : ""}</span>
                          )}
                          {(lead?.website || lead?.instagram) && (
                            <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px]" onClick={(e) => e.stopPropagation()}>
                              {lead?.website && (
                                <a href={extUrl(lead.website)} target="_blank" rel="noopener noreferrer" data-testid="lead-website-link"
                                  title={lead.website} className="flex max-w-[220px] items-center gap-1 text-muted-foreground hover:text-brand hover:underline">
                                  <Globe className="h-3 w-3 shrink-0" /> <span className="truncate">{String(lead.website).replace(/^https?:\/\//i, "").replace(/\/$/, "")}</span>
                                </a>
                              )}
                              {lead?.instagram && (
                                <a href={igUrl(lead.instagram)} target="_blank" rel="noopener noreferrer" data-testid="lead-instagram-link"
                                  title="Apri profilo Instagram" className="flex max-w-[180px] items-center gap-1 text-muted-foreground hover:text-brand hover:underline">
                                  <Instagram className="h-3 w-3 shrink-0" /> <span className="truncate">@{String(lead.instagram).replace(/^https?:\/\/(www\.)?instagram\.com\//i, "").replace(/^@/, "").replace(/\/$/, "")}</span>
                                </a>
                              )}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3 text-muted-foreground">
                          <div className="text-foreground">{lead?.city || "N/D"}</div>
                          <div className="text-[11px]">{lead?.region || ""}</div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2.5">
                            {lead?.email
                              ? <Mail className="h-4 w-4 text-brand" data-testid="lead-icon-email" />
                              : <Mail className="h-4 w-4 text-muted-foreground/25" />}
                            {waDigitsOf(lead)
                              ? <a href={`https://wa.me/${waDigitsOf(lead)}`} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} data-testid="lead-icon-whatsapp"><MessageCircle className="h-4 w-4 text-[#25D366]" /></a>
                              : (kindOf(lead) === "landline"
                                ? <Phone className="h-4 w-4 text-amber-500" data-testid="lead-icon-landline" />
                                : <MessageCircle className="h-4 w-4 text-muted-foreground/25" />)}
                          </div>
                        </td>
                        <td className="px-4 py-3"><ScoreBadge score={lead?.fit_score} /></td>
                        <td className="px-4 py-3">
                          <StatusBadge status={lead?.status || "nuovo"} />
                          {lead?.status === "contattato" && lead?.last_contacted_at && (
                            <span data-testid="lead-contacted-date" className="mt-1 block text-[10px] text-muted-foreground">{fmtDateTime(lead.last_contacted_at)}</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                          <div className="flex items-center justify-end gap-2">
                            <Button size="sm" data-testid="outreach-btn" onClick={() => openOutreach(lead)}
                              className="rounded-full bg-primary text-xs font-semibold text-primary-foreground hover:bg-primary/90">
                              <Sparkles className="mr-1 h-3.5 w-3.5" /> Outreach
                            </Button>
                            <button type="button" data-testid="lead-delete-btn" onClick={() => setLeadToDelete(lead)}
                              className="rounded-full p-1 text-muted-foreground hover:bg-red-500/10 hover:text-brema-red" title="Elimina rivenditore">
                              <Trash2 className="h-4 w-4" />
                            </button>
                            <button type="button" data-testid="lead-detail-btn" onClick={() => setDetailLead(lead)}
                              className="rounded-full p-1 text-muted-foreground hover:bg-brema-hover hover:text-foreground" title="Dettagli">
                              <ChevronRight className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </ErrorBoundary>
          )}
        </TabsContent>

        {/* ================= TAB CAMPAGNE ================= */}
        <TabsContent value="campagne">
          {draftBatches.length === 0 ? (
            <div className="rounded-xl border border-dashed border-border bg-brema-card/50 p-12 text-center text-muted-foreground">
              Nessuna campagna in preparazione. Genera bozze dalla tab Rivenditori (Outreach singolo o Campagna di massa).
            </div>
          ) : (
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
              {draftBatches.map((b) => (
                <div key={b._id} data-testid="campaign-batch-card" onClick={() => openBatch(b)}
                  className="cursor-pointer rounded-xl border border-border bg-brema-card p-5 transition-colors hover:border-primary/40">
                  <div className="mb-2 flex items-start justify-between gap-2">
                    <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${batchStatusClass(b.status)}`}>{batchStatusLabel(b.status)}</span>
                    <button data-testid="batch-delete-btn" onClick={(e) => deleteBatch(b, e)}
                      className="text-muted-foreground hover:text-danger"><Trash2 className="h-4 w-4" /></button>
                  </div>
                  <p className="text-xs text-muted-foreground">{fmtDateTime(b.created_at)}</p>
                  <h4 className="mt-1 font-semibold text-foreground">{b.title}</h4>
                  {b.sample_subject && <p className="mt-1 line-clamp-2 text-sm text-foreground">Oggetto: {b.sample_subject}</p>}
                  <div className="mt-3 flex items-center gap-3 text-sm text-foreground">
                    <span className="flex items-center gap-1"><Users className="h-3.5 w-3.5 text-brand" /> {b.recipient_count} destinatari</span>
                    <span className="text-muted-foreground">·</span>
                    <span>{b.sent_count || 0} inviate</span>
                  </div>
                  <Button size="sm" data-testid="open-batch-btn"
                    className="mt-4 w-full rounded-full bg-primary font-semibold text-primary-foreground hover:bg-primary/90">
                    Apri e gestisci
                  </Button>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        {/* ================= TAB STORICO INVII ================= */}
        <TabsContent value="storico">
          {history.length > 0 && <EmailFunnel history={history} />}
          {history.length === 0 ? (
            <div className="rounded-xl border border-dashed border-border bg-brema-card/50 p-12 text-center text-muted-foreground">
              Nessuna email inviata. Le comunicazioni spedite compariranno qui come registro.
            </div>
          ) : (
            <div className="space-y-3">
              <div className="flex items-center gap-2" data-testid="history-bulk-toolbar">
                <span className="text-xs text-muted-foreground" data-testid="history-selected-count">{historySel.size} selezionati</span>
                {historySel.size > 0 && (
                  <Button size="sm" variant="danger" data-testid="history-delete-selected-btn" onClick={() => setHistDelOpen(true)}
                    className="rounded-full text-xs">
                    <Trash2 className="mr-1 h-3.5 w-3.5" /> Elimina selezionati ({historySel.size})
                  </Button>
                )}
                <Button size="sm" variant="secondary" data-testid="clean-false-sends-btn" onClick={cleanFalseSends} disabled={cleaningFalse}
                  title="Ripristina le email segnate 'Inviata' ma senza un destinatario reale"
                  className="ml-auto rounded-full border border-amber-500/40 bg-transparent text-xs text-amber-700 dark:text-amber-300 hover:bg-amber-500/10">
                  {cleaningFalse ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <AlertTriangle className="mr-1 h-3.5 w-3.5" />} Pulisci invii falsi
                </Button>
              </div>
              <div className="overflow-x-auto rounded-xl border border-border bg-brema-card" data-testid="history-table">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-muted-foreground">
                    <th className="px-4 py-3 font-medium">
                      <Checkbox checked={history.length > 0 && historySel.size === history.length} onCheckedChange={toggleHistAll}
                        data-testid="history-select-all" className="border-border data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground" />
                    </th>
                    <th className="px-4 py-3 font-medium">Data invio</th>
                    <th className="px-4 py-3 font-medium">Rivenditore</th>
                    <th className="px-4 py-3 font-medium">Email / Telefono</th>
                    <th className="px-4 py-3 font-medium">Oggetto</th>
                    <th className="px-4 py-3 font-medium">Stato consegna</th>
                    <th className="px-4 py-3 font-medium text-right">Dettaglio</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map((m) => (
                    <tr key={m._id} data-testid="history-row" onClick={() => setHistoryDetail(m)}
                      className="cursor-pointer border-b border-border text-foreground last:border-0 hover:bg-muted">
                      <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                        <Checkbox checked={historySel.has(m._id)} onCheckedChange={() => toggleHistSel(m._id)}
                          data-testid="history-row-checkbox" className="border-border data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground" />
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-muted-foreground">{fmtDateTime(m.sent_at)}</td>
                      <td className="px-4 py-3 font-medium text-foreground">{m.lead_name || "—"}</td>
                      <td className="px-4 py-3">{m.recipient ? <a href={recipientHref(m.recipient)} {...(isPhoneRecipient(m.recipient) ? { target: "_blank", rel: "noopener noreferrer" } : {})} onClick={(e) => e.stopPropagation()} className="hover:text-brand" data-testid="history-recipient-link">{m.recipient}</a> : "—"}</td>
                      <td className="max-w-[280px] truncate px-4 py-3 text-foreground">{m.subject || "—"}</td>
                      <td className="px-4 py-3">
                        <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${deliveryClass(m.delivery_status || "inviata")}`}>
                          {DELIVERY_LABELS[m.delivery_status] || "Inviata"}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <Button size="sm" variant="secondary" data-testid="history-detail-btn"
                          onClick={(e) => { e.stopPropagation(); setHistoryDetail(m); }}
                          className="rounded-full border border-border bg-transparent text-xs hover:bg-brema-hover">
                          <Eye className="mr-1 h-3.5 w-3.5" /> Vedi
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            </div>
          )}
        </TabsContent>

        {/* ================= TAB ESCLUSI ================= */}
        <TabsContent value="esclusi">
          <div className="mb-4 rounded-2xl border border-border bg-brema-card p-5" data-testid="excluded-box">
            <div className="mb-3 flex flex-wrap items-center gap-2">
              <X className="h-5 w-5 text-amber-500" />
              <h3 className="text-sm font-semibold">Contatti esclusi (fuori target Brema Bike)</h3>
              <span className="hidden text-xs text-muted-foreground sm:inline">· attività chiuse, catene, produttori, custom/teamwear, officine e regole KB scartati automaticamente</span>
              {excluded.length > 0 && (
                <Button size="sm" variant="danger" data-testid="clear-excluded-btn" onClick={clearExcluded}
                  className="ml-auto h-8 rounded-full text-xs">
                  <Trash2 className="mr-1.5 h-3.5 w-3.5" /> Svuota archivio
                </Button>
              )}
            </div>
            {loadingExcluded ? (
              <div className="flex items-center gap-2 p-6 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Caricamento…</div>
            ) : excluded.length === 0 ? (
              <p className="rounded-lg border border-dashed border-border p-6 text-center text-sm text-muted-foreground" data-testid="excluded-empty">
                Nessun contatto escluso. Qui finiscono i candidati scartati dalla pipeline di qualificazione (con il motivo) durante le ricerche "bici".
              </p>
            ) : (
              <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                {excluded.map((e) => (
                  <div key={e._id} data-testid="excluded-card" className="flex flex-col rounded-xl border border-border bg-brema-bg p-4">
                    <div className="mb-1 flex items-start justify-between gap-2">
                      <h4 className="font-semibold leading-tight text-foreground">{e.name}</h4>
                      <span className="shrink-0 rounded-full bg-amber-500/15 px-2 py-0.5 text-[10px] font-semibold text-amber-500">Escluso</span>
                    </div>
                    <p className="flex items-center gap-1 text-xs text-muted-foreground"><MapPin className="h-3 w-3" /> {e.city || "N/D"}, {e.region || "N/D"}</p>
                    {e.business_type_label && <span className="mt-1 w-fit rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium text-foreground" data-testid="excluded-business-type">{e.business_type_label}</span>}
                    {e.exclusion_reason ? (
                      <p className="mt-1 text-[12px] text-foreground" data-testid="excluded-reason">Motivo: {e.exclusion_reason}</p>
                    ) : e.description ? (
                      <p className="mt-1 line-clamp-2 text-[13px] text-foreground">{e.description}</p>
                    ) : null}
                    {Array.isArray(e.mech_keywords) && e.mech_keywords.length > 0 && (
                      <p className="mt-1 text-[11px] text-muted-foreground">Motivo: {e.mech_keywords.slice(0, 4).join(", ")}</p>
                    )}
                    <div className="mt-auto flex items-center gap-2 pt-2">
                      {e.website && (
                        <a href={extUrl(e.website)} target="_blank" rel="noopener noreferrer" data-testid="excluded-website-link"
                          className="flex items-center gap-1 text-xs text-foreground hover:text-brand hover:underline">
                          <Globe className="h-3.5 w-3.5" /> Sito
                        </a>
                      )}
                      <Button size="sm" variant="danger" data-testid="delete-excluded-btn" onClick={() => deleteExcluded(e._id)}
                        className="ml-auto h-7 rounded-full text-xs">
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* ===== Storico Invii detail dialog (timeline) ===== */}
      <Dialog open={!!historyDetail} onOpenChange={(o) => !o && setHistoryDetail(null)}>
        <DialogContent className="max-w-lg border-border bg-brema-card" data-testid="sales-history-detail-dialog">
          <DialogHeader>
            <DialogTitle className="text-foreground">{historyDetail?.lead_name || "Invio"}</DialogTitle>
            <DialogDescription className="flex flex-wrap items-center gap-2">
              <span>{fmtDateTime(historyDetail?.sent_at)}</span>
              {historyDetail?.recipient && <><span>·</span><a href={recipientHref(historyDetail.recipient)} {...(isPhoneRecipient(historyDetail.recipient) ? { target: "_blank", rel: "noopener noreferrer" } : {})} className="hover:text-brand" data-testid="sales-history-recipient-link">{historyDetail.recipient}</a></>}
              <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${deliveryClass(historyDetail?.delivery_status || "inviata")}`}>
                {DELIVERY_LABELS[historyDetail?.delivery_status] || "Inviata"}
              </span>
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[60vh] space-y-4 overflow-y-auto pr-1">
            <div>
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Oggetto</p>
              <p className="text-sm font-medium text-foreground" data-testid="sales-history-subject">{historyDetail?.subject || "—"}</p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Testo inviato</p>
              <MessageBody text={historyDetail?.email_body || historyDetail?.social_dm || ""} className="rounded-lg bg-brema-bg p-3" testId="sales-history-body" />
            </div>
            <div>
              <p className="mb-2 text-[10px] uppercase tracking-wider text-muted-foreground">Timeline consegna</p>
              <ol className="relative ml-2 border-l border-border" data-testid="sales-history-timeline">
                {(() => {
                  const st = historyDetail?.delivery_status || "inviata";
                  const bounced = ["rimbalzata", "reclamo"].includes(st);
                  const rank = DELIVERY_RANK[st] || 1;
                  return TIMELINE_STEPS.map((step) => {
                    const reached = rank >= DELIVERY_RANK[step.key];
                    const isCurrent = st === step.key;
                    return (
                      <li key={step.key} className="mb-3 ml-4" data-testid={`timeline-${step.key}`}>
                        <span className={`absolute -left-[7px] flex h-3.5 w-3.5 items-center justify-center rounded-full ${reached ? "bg-positive" : "bg-muted border border-border"}`} />
                        <p className={`text-sm font-medium ${reached ? "text-foreground" : "text-muted-foreground"}`}>{step.label}</p>
                        {step.key === "inviata" && historyDetail?.sent_at && (
                          <p className="text-xs text-muted-foreground">{fmtDateTime(historyDetail.sent_at)}</p>
                        )}
                        {isCurrent && step.key !== "inviata" && historyDetail?.delivery_updated_at && (
                          <p className="text-xs text-muted-foreground">{fmtDateTime(historyDetail.delivery_updated_at)}</p>
                        )}
                      </li>
                    );
                  });
                })()}
                {["rimbalzata", "reclamo"].includes(historyDetail?.delivery_status) && (
                  <li className="mb-1 ml-4" data-testid="timeline-bounced">
                    <span className="absolute -left-[7px] h-3.5 w-3.5 rounded-full bg-red-500" />
                    <p className="text-sm font-medium text-red-600 dark:text-red-300">{DELIVERY_LABELS[historyDetail.delivery_status]}</p>
                    {historyDetail?.delivery_updated_at && <p className="text-xs text-muted-foreground">{fmtDateTime(historyDetail.delivery_updated_at)}</p>}
                  </li>
                )}
              </ol>
            </div>
          </div>
          <div className="flex justify-end border-t border-border pt-3">
            <Button variant="danger" size="sm" data-testid="sales-history-delete-btn"
              onClick={() => { if (historyDetail?._id) { setHistorySel(new Set([historyDetail._id])); setHistDelOpen(true); } }}
              className="rounded-full text-xs">
              <Trash2 className="mr-1 h-3.5 w-3.5" /> Elimina invio
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ===== Conferma eliminazione Storico Invii ===== */}
      <Dialog open={histDelOpen} onOpenChange={(o) => !o && setHistDelOpen(false)}>
        <DialogContent className="max-w-md border-border bg-brema-card" data-testid="history-delete-dialog">
          <DialogHeader>
            <DialogTitle className="text-foreground">Elimina invii</DialogTitle>
            <DialogDescription>
              Vuoi eliminare <strong className="text-foreground">{historySel.size}</strong> {historySel.size === 1 ? "invio" : "invii"} dallo Storico? L'azione è irreversibile.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="secondary" data-testid="history-delete-cancel" onClick={() => setHistDelOpen(false)}
              className="rounded-full border border-border bg-transparent">Annulla</Button>
            <Button data-testid="history-delete-confirm-btn" disabled={deletingHist} onClick={deleteHistorySelected}
              className="rounded-full bg-brema-red font-semibold text-white hover:bg-brema-red/90">
              {deletingHist ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Trash2 className="mr-1.5 h-4 w-4" /> Elimina</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ===== Detail drawer (pannello laterale) ===== */}
      <Sheet open={!!detailLead} onOpenChange={(o) => !o && setDetailLead(null)}>
        <SheetContent side="right" className="w-full overflow-y-auto border-border bg-brema-card sm:max-w-xl" data-testid="lead-detail-dialog">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2 text-foreground">
              <Store className="h-4 w-4 text-brand" /> {detailLead?.name}
            </SheetTitle>
            <SheetDescription className="flex flex-wrap items-center gap-1.5">
              <MapPin className="h-3.5 w-3.5" /> {detailLead?.city || "N/D"}, {detailLead?.region || "N/D"}
              {detailLead?.nation && detailLead?.nation !== detailLead?.region && (
                <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] text-foreground" data-testid="lead-nation-badge">{detailLead.nation}</span>
              )}
              <ScoreBadge score={detailLead?.fit_score} />
            </SheetDescription>
          </SheetHeader>

          <div className="mt-5 space-y-5 text-sm text-foreground">
            {!detailLead?._id && detailLead?.temp_id ? (
              /* Temporary search result: not yet in the archive */
              <div className="flex flex-wrap items-center gap-3 rounded-xl border border-primary/40 bg-primary/5 p-3" data-testid="detail-temp-banner">
                <div className="flex-1 text-xs text-muted-foreground">
                  Risultato di ricerca <strong className="text-foreground">non ancora in archivio</strong>. Valutalo e salvalo per gestirne stato, note e storico.
                </div>
                {detailLead?.already_saved ? (
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-positive/10 px-2.5 py-1 text-xs font-medium text-positive"><Check className="h-3.5 w-3.5" /> In archivio</span>
                ) : (
                  <Button size="sm" data-testid="detail-save-result-btn" disabled={savingResults}
                    onClick={async () => { await saveOneResult(detailLead); setDetailLead((p) => ({ ...p, already_saved: true })); }}
                    className="rounded-full bg-primary text-xs font-semibold text-primary-foreground hover:bg-primary/90">
                    <Plus className="mr-1.5 h-3.5 w-3.5" /> Salva in Archivio
                  </Button>
                )}
              </div>
            ) : (
              /* Stato campagna (solo per rivenditori salvati) */
              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">Stato campagna</Label>
                <Select value={detailLead?.status || "nuovo"}
                  onValueChange={(v) => { changeStatus(detailLead, v); setDetailLead((p) => ({ ...p, status: v })); }}>
                  <SelectTrigger className="h-9" data-testid="detail-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {STATUSES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}

            {detailLead?.description && <p className="leading-relaxed text-foreground">{detailLead.description}</p>}
            {typeof detailLead?.rating === "number" && (
              <p className="flex items-center gap-1.5 text-brand">
                <Star className="h-3.5 w-3.5 fill-brema-yellow" /> {detailLead.rating}
                {detailLead?.reviews ? <span className="text-muted-foreground">({detailLead.reviews} recensioni)</span> : null}
              </p>
            )}

            {/* Contatti */}
            <div className="space-y-2 rounded-xl border border-border bg-brema-bg p-3">
              {detailLead?.address && (
                <p className="flex items-start gap-1.5"><MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" /> {detailLead.address}</p>
              )}
              {(detailLead?.phone_e164 || detailLead?.phone) && (
                <a href={`tel:${detailLead.phone}`} data-testid="detail-phone-link" className="flex items-center gap-1.5 hover:text-brand">
                  <Phone className="h-3.5 w-3.5 shrink-0" /> {formatE164Display(detailLead.phone_e164) || detailLead.phone}
                </a>
              )}
              {detailLead?.email ? (
                <a href={`mailto:${detailLead.email}`} data-testid="detail-email-link" className="flex items-center gap-1.5 hover:text-brand">
                  <Mail className="h-3.5 w-3.5 shrink-0 text-brand" /> <span className="truncate">{detailLead.email}</span>
                </a>
              ) : (
                <span className="flex items-center gap-1.5 text-xs text-muted-foreground"><Mail className="h-3.5 w-3.5 shrink-0" /> Email non disponibile</span>
              )}
              {detailLead?.website && (
                <a href={extUrl(detailLead.website)} target="_blank" rel="noopener noreferrer" data-testid="detail-website-link" className="flex items-center gap-1.5 hover:text-brand">
                  <Globe className="h-3.5 w-3.5 shrink-0" /> <span className="truncate">{detailLead.website}</span>
                </a>
              )}
              {waDigitsOf(detailLead) && (
                <a href={`https://wa.me/${waDigitsOf(detailLead)}`} target="_blank" rel="noopener noreferrer" data-testid="detail-whatsapp-quick"
                  className="inline-flex w-fit items-center gap-1.5 rounded-full bg-[#25D366]/15 px-2.5 py-1 text-xs font-semibold text-[#128C4A] hover:bg-[#25D366]/25 dark:text-[#25D366]">
                  <MessageCircle className="h-3.5 w-3.5" /> Apri WhatsApp
                </a>
              )}
            </div>

            {/* Modifica contatti in archivio (solo rivenditori salvati) */}
            {detailLead?._id && (
              <div className="space-y-3 rounded-xl border border-border bg-brema-bg p-3" data-testid="detail-contacts-edit">
                <Label className="flex items-center gap-1.5 text-xs font-semibold text-foreground"><Mail className="h-3.5 w-3.5 text-brand" /> Modifica contatti</Label>
                <div className="space-y-2">
                  <div className="space-y-1">
                    <Label className="text-[11px] text-muted-foreground">Email principale</Label>
                    <Input data-testid="detail-email-input" type="email" value={detailLead?.email || ""}
                      onChange={(e) => setDetailLead((p) => ({ ...p, email: e.target.value }))}
                      placeholder="email@negozio.it" className="h-9 text-sm" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-[11px] text-muted-foreground">Telefono</Label>
                    <Input data-testid="detail-phone-input" value={detailLead?.phone || ""}
                      onChange={(e) => setDetailLead((p) => ({ ...p, phone: e.target.value }))}
                      placeholder="+39 333 1234567" className="h-9 text-sm" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-[11px] text-muted-foreground">Sito web</Label>
                    <Input data-testid="detail-website-input" value={detailLead?.website || ""}
                      onChange={(e) => setDetailLead((p) => ({ ...p, website: e.target.value }))}
                      placeholder="www.negozio.it" className="h-9 text-sm" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-[11px] text-muted-foreground">Riferimenti aggiuntivi</Label>
                    <Textarea data-testid="detail-contactref-input" value={detailLead?.contact_ref || ""} rows={2}
                      onChange={(e) => setDetailLead((p) => ({ ...p, contact_ref: e.target.value }))}
                      placeholder="Es. Mario Rossi (resp. acquisti) · mario@negozio.it · +39 ..." className="text-sm" />
                  </div>
                </div>
                <Button size="sm" variant="secondary" data-testid="detail-contacts-save" onClick={saveLeadContacts} disabled={savingContacts}
                  className="rounded-full border border-border bg-transparent text-xs text-foreground hover:bg-brema-hover">
                  {savingContacts ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Save className="mr-1.5 h-3.5 w-3.5" />} Salva contatti
                </Button>
              </div>
            )}

            {/* WhatsApp override (solo rivenditori salvati) */}
            {detailLead?._id && (
              <div className="space-y-1.5">
                <Label className="flex items-center gap-1.5 text-xs text-muted-foreground"><MessageCircle className="h-3.5 w-3.5 text-[#25D366]" /> Numero WhatsApp (override)</Label>
                <div className="flex items-center gap-2">
                  <Input data-testid="detail-whatsapp-input" value={detailLead?.whatsapp || ""}
                    onChange={(e) => setDetailLead((p) => ({ ...p, whatsapp: e.target.value }))}
                    placeholder={detailLead?.phone || "+39 333 1234567"} className="h-9 text-sm" />
                  <Button size="sm" variant="secondary" data-testid="detail-whatsapp-save" onClick={saveLeadWhatsapp} disabled={savingWa}
                    className="rounded-full border border-border bg-transparent text-xs text-foreground hover:bg-brema-hover">
                    {savingWa ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
                  </Button>
                </div>
              </div>
            )}

            {/* Note (solo rivenditori salvati) */}
            {detailLead?._id && (
              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">Note & accordi</Label>
                <Textarea data-testid="detail-notes-input" value={detailLead?.notes || ""} rows={3}
                  onChange={(e) => setDetailLead((p) => ({ ...p, notes: e.target.value }))}
                  placeholder="Appunti, accordi commerciali, promemoria…" className="text-sm" />
                <Button size="sm" variant="secondary" data-testid="detail-notes-save" onClick={saveLeadNotes} disabled={savingNotes}
                  className="rounded-full border border-border bg-transparent text-xs text-foreground hover:bg-brema-hover">
                  {savingNotes ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Save className="mr-1.5 h-3.5 w-3.5" />} Salva note
                </Button>
              </div>
            )}

            {detailLead?.last_contacted_at && (
              <p className="flex items-center gap-1.5 text-muted-foreground"><Clock className="h-3.5 w-3.5" /> Ultimo contatto: {fmtDateTime(detailLead.last_contacted_at)}</p>
            )}
            {(Array.isArray(detailLead?.signals) ? detailLead.signals : []).length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {detailLead.signals.map((s, idx) => (
                  <span key={idx} className="rounded-full bg-muted px-2.5 py-1 text-xs font-medium text-foreground">{s}</span>
                ))}
              </div>
            )}

            {/* Perché è in target (qualifica apparel) */}
            {detailLead?.qualification && (
              <div className="rounded-xl border border-border bg-brema-bg p-3" data-testid="detail-target-reason">
                <div className="mb-2 flex items-center gap-1.5 text-xs font-semibold text-foreground">
                  <Shirt className="h-3.5 w-3.5 text-brand" /> Perché è in target
                  {detailLead.qualification === "qualificato" && <span className="rounded-full bg-positive/15 px-2 py-0.5 text-[10px] font-semibold text-positive" data-testid="detail-qual-badge">Qualificato ≥70</span>}
                  {detailLead.qualification === "brand" && <span className="rounded-full bg-positive/15 px-2 py-0.5 text-[10px] font-semibold text-positive">Brand 100%</span>}
                  {detailLead.qualification === "apparel" && <span className="rounded-full bg-brand/15 px-2 py-0.5 text-[10px] font-semibold text-brand">Reparto apparel</span>}
                  {detailLead.qualification === "da_verificare" && <span className="rounded-full bg-amber-500/15 px-2 py-0.5 text-[10px] font-semibold text-amber-500" data-testid="detail-qual-badge">Da verificare</span>}
                </div>
                <QualificationEvidence lead={detailLead} />
                {Array.isArray(detailLead.brands) && detailLead.brands.length > 0 && (
                  <div className="mb-2">
                    <p className="mb-1 text-[11px] text-muted-foreground">Marchi trovati sul sito</p>
                    <div className="flex flex-wrap gap-1.5">
                      {detailLead.brands.map((b, i) => (
                        <span key={i} className="inline-flex items-center gap-1 rounded-full bg-positive/10 px-2 py-0.5 text-[11px] font-medium text-positive"><BadgeCheck className="h-3 w-3" /> {b}</span>
                      ))}
                    </div>
                  </div>
                )}
                {Array.isArray(detailLead.match_keywords) && detailLead.match_keywords.length > 0 && (
                  <div>
                    <p className="mb-1 text-[11px] text-muted-foreground">Keyword abbigliamento/accessori rilevate</p>
                    <div className="flex flex-wrap gap-1.5">
                      {detailLead.match_keywords.map((k, i) => (
                        <span key={i} className="rounded-full bg-muted px-2 py-0.5 text-[11px] text-foreground">{k}</span>
                      ))}
                    </div>
                  </div>
                )}
                {(!detailLead.brands || detailLead.brands.length === 0) && (!detailLead.match_keywords || detailLead.match_keywords.length === 0) && !detailLead.qualifier_engine && (
                  <p className="text-[11px] text-muted-foreground">Reparto abbigliamento non rilevato automaticamente: da verificare manualmente sul sito del negozio.</p>
                )}
              </div>
            )}

            {/* Storico contatti (solo rivenditori salvati) */}
            {detailLead?._id && (
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">Storico invii</Label>
                {(() => {
                  const lh = history.filter((h) => (detailLead && (h.lead_id === detailLead._id || h.lead_name === detailLead.name)));
                  if (!lh.length) return <p className="text-xs text-muted-foreground" data-testid="detail-history-empty">Nessun invio registrato per questo rivenditore.</p>;
                  return (
                    <div className="space-y-1.5" data-testid="detail-history-list">
                      {lh.slice(0, 8).map((h) => (
                        <div key={h._id} className="flex items-center justify-between gap-2 rounded-lg border border-border bg-brema-bg px-3 py-2 text-xs">
                          <div className="min-w-0">
                            <div className="truncate font-medium text-foreground">{h.subject || "(senza oggetto)"}</div>
                            <div className="text-muted-foreground">{fmtDateTime(h.sent_at)}</div>
                          </div>
                          <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-medium ${deliveryClass(h.delivery_status || "inviata")}`}>
                            {DELIVERY_LABELS[h.delivery_status] || "Inviata"}
                          </span>
                        </div>
                      ))}
                    </div>
                  );
                })()}
              </div>
            )}

            {/* Mappa */}
            <div className="overflow-hidden rounded-xl border border-border">
              <iframe title="Mappa rivenditore" data-testid="lead-map-iframe" src={mapSrc(detailLead)}
                className="h-56 w-full" style={{ border: 0 }}
                loading="lazy" referrerPolicy="no-referrer-when-downgrade" allowFullScreen />
            </div>
          </div>

          <div className="mt-6 flex flex-wrap items-center justify-end gap-2 border-t border-border pt-4">
            {detailLead?._id && (
              <Button variant="danger" size="sm" data-testid="detail-delete-btn"
                onClick={() => setLeadToDelete(detailLead)}
                className="mr-auto rounded-full">
                <Trash2 className="mr-1 h-3.5 w-3.5" /> Elimina
              </Button>
            )}
            {detailLead?.maps_url && (
              <a href={detailLead.maps_url} target="_blank" rel="noopener noreferrer" data-testid="detail-maps-link">
                <Button variant="secondary" size="sm" className="rounded-full border border-border bg-transparent text-foreground hover:bg-brema-hover">
                  <MapPin className="mr-1 h-3.5 w-3.5" /> Google Maps
                </Button>
              </a>
            )}
            <Button size="sm" data-testid="detail-outreach-btn"
              onClick={() => { const l = detailLead; if (l?._id) { setDetailLead(null); openOutreach(l); } else { outreachFromResult(l); } }}
              className="rounded-full bg-primary font-semibold text-primary-foreground hover:bg-primary/90">
              <Sparkles className="mr-1 h-3.5 w-3.5" /> Genera Outreach
            </Button>
          </div>
        </SheetContent>
      </Sheet>

      {/* ===== Gestione marchi di riferimento (Admin) ===== */}
      <Dialog open={brandsOpen} onOpenChange={setBrandsOpen}>
        <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto border-border bg-brema-card" data-testid="brands-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-foreground"><BadgeCheck className="h-4 w-4 text-brand" /> Marchi di riferimento</DialogTitle>
            <DialogDescription>
              Se uno di questi marchi compare sul sito di un negozio (categoria "bici"), il lead riceve Match 100%.
              I marchi "ambigui" (parole corte/comuni) contano solo se il sito ha anche contenuti di abbigliamento.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex flex-wrap items-end gap-2">
              <div className="flex-1 space-y-1.5">
                <Label>Nuovo marchio</Label>
                <Input value={newBrand} onChange={(e) => setNewBrand(e.target.value)} placeholder="Es. Nalini" data-testid="new-brand-input"
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addBrand(); } }} />
              </div>
              <label className="flex items-center gap-1.5 pb-2 text-xs text-muted-foreground">
                <Checkbox checked={newBrandAmb} onCheckedChange={(v) => setNewBrandAmb(!!v)} data-testid="new-brand-ambiguous"
                  className="border-border data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground" />
                Ambiguo
              </label>
              <Button size="sm" data-testid="add-brand-btn" onClick={addBrand} disabled={!newBrand.trim()}
                className="mb-0.5 rounded-full bg-primary text-xs font-semibold text-primary-foreground hover:bg-primary/90">
                <Plus className="mr-1 h-3.5 w-3.5" /> Aggiungi
              </Button>
            </div>
            <div className="space-y-2" data-testid="brands-list">
              {brands.length === 0 ? (
                <p className="rounded-lg border border-dashed border-border p-4 text-center text-xs text-muted-foreground">Nessun marchio configurato.</p>
              ) : brands.map((b) => (
                <div key={b._id} data-testid="brand-row" className="flex items-center justify-between gap-2 rounded-lg border border-border bg-brema-bg px-3 py-2">
                  <span className="flex items-center gap-2 text-sm font-medium text-foreground">
                    <BadgeCheck className="h-3.5 w-3.5 text-brand" /> {b.name}
                    {b.ambiguous && <span className="rounded-full bg-amber-500/15 px-2 py-0.5 text-[10px] font-medium text-amber-500">ambiguo</span>}
                  </span>
                  <Button size="sm" variant="danger" data-testid="delete-brand-btn" onClick={() => deleteBrand(b)}
                    className="h-7 rounded-full text-xs">
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              ))}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* ===== Conferma eliminazione singolo rivenditore ===== */}
      <Dialog open={!!leadToDelete} onOpenChange={(o) => !o && setLeadToDelete(null)}>
        <DialogContent className="max-w-md border-border bg-brema-card" data-testid="lead-delete-dialog">
          <DialogHeader>
            <DialogTitle className="text-foreground">Elimina rivenditore</DialogTitle>
            <DialogDescription>
              Vuoi eliminare <strong className="text-foreground">{leadToDelete?.name}</strong> dall'archivio? L'azione non è reversibile.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="secondary" data-testid="lead-delete-cancel" onClick={() => setLeadToDelete(null)}
              className="rounded-full border border-border bg-transparent">Annulla</Button>
            <Button data-testid="lead-delete-confirm-btn" disabled={deletingOne} onClick={deleteOneLead}
              className="rounded-full bg-brema-red font-semibold text-white hover:bg-brema-red/90">
              {deletingOne ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Trash2 className="mr-1.5 h-4 w-4" /> Elimina</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ===== Single Outreach dialog (unified composer) ===== */}
      <EmailComposer
        open={!!dialogLead}
        onOpenChange={(o) => !o && setDialogLead(null)}
        target="leads"
        contact={dialogLead}
        templateModule="rivenditori"
        defaultTo={dialogLead?.email || ""}
        title={`Contatta · ${dialogLead?.name || ""}`}
        channelHint="Scegli il canale: E-mail (AI, CC/CCN e allegati) oppure WhatsApp Business (template di primo contatto)."
        allowWhatsapp
        waDefaults={{
          phone: dialogLead?.whatsapp || dialogLead?.phone || "",
          name: dialogLead?.name || "",
          link: integrations?.whatsapp_catalog_link || "",
          template: integrations?.whatsapp_template || "",
        }}
        waTemplates={waTemplates}
        onSent={() => { loadLeads(); loadBatches(); loadHistory(); }}
      />

      <Dialog open={tplManagerOpen} onOpenChange={setTplManagerOpen}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto border-border bg-brema-card" data-testid="wa-templates-dialog">
          <DialogHeader>
            <DialogTitle className="text-foreground">Modelli messaggio WhatsApp</DialogTitle>
            <DialogDescription>
              Crea più versioni del primo contatto. Segnaposto: {"{nome}"} · {"{citta}"} · {"{regione}"} · {"{categoria}"} · {"{link}"} · {"{link_press_lounge}"} · {"{link_ambassador_lounge}"}.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              {waTemplates.length === 0 ? (
                <p className="text-sm text-muted-foreground" data-testid="wa-templates-empty">Nessun modello salvato. Creane uno qui sotto.</p>
              ) : waTemplates.map((t) => (
                <div key={t._id} data-testid="wa-template-row" className="flex items-start justify-between gap-2 rounded-lg border border-border bg-brema-bg p-3">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-foreground">{t.title} <span className="ml-1 text-[11px] font-normal text-muted-foreground">({t.meta_template_name} · {t.language})</span></p>
                    <p className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">{t.preview_text || "—"}</p>
                  </div>
                  <div className="flex shrink-0 gap-1">
                    <Button size="sm" variant="secondary" data-testid="wa-template-edit" onClick={() => editTpl(t)} className="rounded-full border border-border bg-transparent text-xs text-foreground hover:bg-brema-hover">Modifica</Button>
                    <Button size="sm" variant="danger" data-testid="wa-template-delete" onClick={() => deleteTpl(t)} className="rounded-full text-xs"><Trash2 className="h-3.5 w-3.5" /></Button>
                  </div>
                </div>
              ))}
            </div>
            <div className="space-y-3 rounded-lg border border-border bg-brema-bg p-4">
              <p className="text-sm font-semibold text-foreground">{tplEditingId ? "Modifica modello" : "Nuovo modello"}</p>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div className="space-y-1.5"><Label>Titolo *</Label><Input data-testid="wa-tpl-title" value={tplForm.title} onChange={(e) => setTplForm((p) => ({ ...p, title: e.target.value }))} placeholder="Es. Amichevole" /></div>
                <div className="space-y-1.5"><Label>Nome template Meta</Label><Input data-testid="wa-tpl-meta" value={tplForm.meta_template_name} onChange={(e) => setTplForm((p) => ({ ...p, meta_template_name: e.target.value }))} placeholder="brema_outreach" /></div>
                <div className="space-y-1.5"><Label>Lingua</Label><Input data-testid="wa-tpl-lang" value={tplForm.language} onChange={(e) => setTplForm((p) => ({ ...p, language: e.target.value }))} placeholder="it" /></div>
                <div className="space-y-1.5"><Label>Link catalogo default</Label><Input data-testid="wa-tpl-link" value={tplForm.default_link} onChange={(e) => setTplForm((p) => ({ ...p, default_link: e.target.value }))} placeholder="https://…" /></div>
              </div>
              <div className="space-y-1.5">
                <Label>Testo anteprima ({"{nome}"} · {"{citta}"} · {"{regione}"} · {"{categoria}"} · {"{link}"})</Label>
                <Textarea data-testid="wa-tpl-preview" value={tplForm.preview_text} onChange={(e) => setTplForm((p) => ({ ...p, preview_text: e.target.value }))} className="min-h-[90px]" placeholder="Ciao {nome}! Scopri il catalogo Brema: {link}" />
              </div>
              <div className="flex justify-end gap-2">
                {tplEditingId && <Button variant="secondary" size="sm" onClick={() => { setTplEditingId(null); setTplForm(emptyTpl); }} className="rounded-full border border-border bg-transparent text-xs text-foreground hover:bg-brema-hover">Annulla</Button>}
                <Button size="sm" data-testid="wa-tpl-save" onClick={saveTpl} disabled={tplSaving} className="rounded-full bg-primary text-xs font-semibold text-primary-foreground hover:bg-primary/90">
                  {tplSaving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : (tplEditingId ? "Salva modifiche" : "Aggiungi modello")}
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* ===== Bulk Outreach Composer (Messaggio Unico / AI) ===== */}
      <Dialog open={bulkComposerOpen} onOpenChange={setBulkComposerOpen}>
        <DialogContent className="max-h-[92vh] max-w-2xl overflow-y-auto border-border bg-brema-card" data-testid="bulk-composer-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-foreground">
              {bulkChannel === "whatsapp" ? <MessageCircle className="h-5 w-5 text-[#25D366]" /> : <Mail className="h-5 w-5 text-brand" />}
              Campagna {bulkChannel === "whatsapp" ? "WhatsApp" : "Email"} · {bulkChannel === "whatsapp" ? bulkTotalCount : selectedCount} destinatari
            </DialogTitle>
            <DialogDescription>
              Invia un unico messaggio a tutti i selezionati (il segnaposto {"{nome}"} viene sostituito automaticamente){bulkChannel === "email" ? ", oppure genera testi personalizzati con l'AI." : "."}
            </DialogDescription>
          </DialogHeader>

          {bulkChannel === "email" && (
            <div className="grid grid-cols-2 gap-2" data-testid="bulk-mode-selector">
              <button type="button" data-testid="bulk-mode-unique" onClick={() => setBulkMode("unique")}
                className={`rounded-xl border p-3 text-left text-sm transition-colors ${bulkMode === "unique" ? "border-primary bg-primary/5 ring-2 ring-primary/30" : "border-border bg-brema-bg hover:border-primary/50"}`}>
                <p className="font-semibold text-foreground">Messaggio unico / Modello</p>
                <p className="mt-0.5 text-xs text-muted-foreground">Stesso testo a tutti, invio in un click</p>
              </button>
              <button type="button" data-testid="bulk-mode-ai" onClick={() => setBulkMode("ai")}
                className={`rounded-xl border p-3 text-left text-sm transition-colors ${bulkMode === "ai" ? "border-primary bg-primary/5 ring-2 ring-primary/30" : "border-border bg-brema-bg hover:border-primary/50"}`}>
                <p className="font-semibold text-foreground">Genera testi individuali con AI</p>
                <p className="mt-0.5 text-xs text-muted-foreground">Bozze personalizzate per negozio (da rivedere)</p>
              </button>
            </div>
          )}

          {(bulkChannel === "whatsapp" || bulkMode === "unique") && (
            <div className="space-y-3">
              <div className="flex flex-wrap items-center gap-2">
                {bulkChannel === "email" ? (
                  <Select onValueChange={applyEmailTemplate}>
                    <SelectTrigger className="h-9 w-60 text-xs" data-testid="bulk-load-email-template"><SelectValue placeholder="Carica da Archivio Modelli…" /></SelectTrigger>
                    <SelectContent>
                      {emailTemplates.length === 0 ? (
                        <SelectItem value="none" disabled>Nessun modello Email salvato</SelectItem>
                      ) : emailTemplates.map((t) => <SelectItem key={t._id} value={t._id}>{t.title}</SelectItem>)}
                    </SelectContent>
                  </Select>
                ) : (
                  <Select value={bulkWaTplId} onValueChange={applyWaTemplate}>
                    <SelectTrigger className="h-9 w-60 text-xs" data-testid="bulk-wa-template-select"><SelectValue placeholder="Carica modello WhatsApp…" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Modello: Standard</SelectItem>
                      {waTemplates.map((t) => <SelectItem key={t._id} value={t._id}>Modello: {t.title}</SelectItem>)}
                    </SelectContent>
                  </Select>
                )}
                <Button variant="secondary" size="sm" data-testid="bulk-save-template-btn" onClick={() => { setSaveTplTitle(""); setSaveTplOpen(true); }}
                  className="rounded-full border border-border bg-transparent text-xs text-foreground hover:bg-brema-hover">
                  <Save className="mr-1 h-3.5 w-3.5" /> Salva come Modello
                </Button>
                {bulkChannel === "whatsapp" && (
                  <Button variant="secondary" size="sm" data-testid="wa-templates-manage-btn" onClick={() => setTplManagerOpen(true)}
                    className="rounded-full border border-border bg-transparent text-xs text-foreground hover:bg-brema-hover">Gestisci modelli</Button>
                )}
              </div>

              {bulkChannel === "email" && (
                <div className="space-y-1.5">
                  <Label>Oggetto Email</Label>
                  <Input data-testid="bulk-subject-input" value={bulkSubject} onChange={(e) => setBulkSubject(e.target.value)} placeholder="Es. Novità Brema Bike per {nome}" />
                </div>
              )}
              {bulkChannel === "email" && (
                !bulkCcOpen ? (
                  <button type="button" data-testid="bulk-ccbcc-toggle" onClick={() => setBulkCcOpen(true)}
                    className="text-xs font-medium text-brand hover:underline">+ Aggiungi CC / CCN</button>
                ) : (
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <div className="space-y-1.5">
                      <Label className="text-xs text-muted-foreground">CC (copia)</Label>
                      <Input data-testid="bulk-cc-input" value={bulkCc} onChange={(e) => setBulkCc(e.target.value)} placeholder="collega@brema.it" />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs text-muted-foreground">CCN (copia nascosta)</Label>
                      <Input data-testid="bulk-bcc-input" value={bulkBcc} onChange={(e) => setBulkBcc(e.target.value)} placeholder="archivio@brema.it" />
                    </div>
                  </div>
                )
              )}
              <div className="space-y-1.5">
                <Label>{bulkChannel === "whatsapp" ? "Testo messaggio (anteprima template)" : "Testo Email"}</Label>
                <Textarea data-testid="bulk-body-input" value={bulkBody} onChange={(e) => setBulkBody(e.target.value)} className="min-h-[150px]"
                  placeholder={bulkChannel === "whatsapp" ? "Ciao {nome} di {citta}! Scopri il catalogo Brema: {link}" : "Gentile {nome},\nsiamo lieti di presentarvi la collezione Brema Bike per {citta}…"} />
                <p className="text-xs text-muted-foreground" data-testid="bulk-vars-hint">
                  Variabili disponibili: <code className="rounded bg-muted px-1">{"{nome}"}</code> <code className="rounded bg-muted px-1">{"{citta}"}</code> <code className="rounded bg-muted px-1">{"{regione}"}</code> <code className="rounded bg-muted px-1">{"{categoria}"}</code> <code className="rounded bg-muted px-1">{"{link_press_lounge}"}</code> <code className="rounded bg-muted px-1">{"{link_ambassador_lounge}"}</code>{bulkChannel === "whatsapp" ? <> <code className="rounded bg-muted px-1">{"{link}"}</code></> : null}
                </p>
              </div>

              {bulkChannel === "email" && (
                <div className="space-y-1.5">
                  <Label>Allegati (Catalogo PDF Brema o file personalizzato)</Label>
                  <AttachmentsInline attachments={bulkAttachments}
                    onAdd={(r) => setBulkAttachments((p) => [...p, r])}
                    onRemove={(sp) => setBulkAttachments((p) => p.filter((a) => a.storage_path !== sp))}
                    testidPrefix="bulk" />
                </div>
              )}

              {bulkChannel === "whatsapp" && (
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <div className="space-y-1.5">
                    <Label>Link catalogo ({"{link}"})</Label>
                    <Input data-testid="bulk-wa-link-input" value={bulkLink} onChange={(e) => setBulkLink(e.target.value)} placeholder="https://…" />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Allega catalogo</Label>
                    <Select value={bulkWaDoc?.storage_path || "none"} onValueChange={(v) => {
                      if (v === "none") { setBulkWaDoc(null); return; }
                      const a = bulkAssets.find((x) => (x.storage_path || x.file_path) === v);
                      if (a) setBulkWaDoc({ storage_path: v, link: fileUrl(v), filename: a.original_filename || a.title || a.name || "Catalogo.pdf" });
                    }}>
                      <SelectTrigger className="h-9 text-xs" data-testid="bulk-wa-doc-select"><SelectValue placeholder="Allega catalogo…" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Nessun allegato</SelectItem>
                        {bulkAssets.map((a) => {
                          const sp = a.storage_path || a.file_path;
                          return <SelectItem key={sp} value={sp}>{a.original_filename || a.title || a.name || "File"}</SelectItem>;
                        })}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}
              {bulkChannel === "whatsapp" && (
                <div className="space-y-1.5">
                  <Label>Lingua template</Label>
                  <Select value={bulkLang} onValueChange={setBulkLang}>
                    <SelectTrigger className="h-9 text-xs" data-testid="bulk-wa-lang-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="auto">Automatica (in base al paese)</SelectItem>
                      <SelectItem value="it">Italiano (it)</SelectItem>
                      <SelectItem value="fr">Français (fr)</SelectItem>
                      <SelectItem value="es">Español (es)</SelectItem>
                      <SelectItem value="de">Deutsch (de)</SelectItem>
                      <SelectItem value="en">English (en)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
              {bulkChannel === "whatsapp" && (
                <div className="rounded-lg border border-dashed border-border bg-brema-bg/50 p-3" data-testid="bulk-add-recipient-box">
                  <Label className="text-xs font-semibold text-foreground">Aggiungi destinatario (solo per questa campagna, non salvato in archivio)</Label>
                  <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-[1fr_1fr_1.2fr_auto]">
                    <Input data-testid="new-recip-name" value={newRecip.name} onChange={(e) => setNewRecip((p) => ({ ...p, name: e.target.value }))} placeholder="Nome" className="h-9 text-xs" />
                    <Input data-testid="new-recip-city" value={newRecip.city} onChange={(e) => setNewRecip((p) => ({ ...p, city: e.target.value }))} placeholder="Città" className="h-9 text-xs" />
                    <Input data-testid="new-recip-phone" value={newRecip.phone} onChange={(e) => setNewRecip((p) => ({ ...p, phone: e.target.value }))}
                      onBlur={() => { const n = normalizePhoneE164(newRecip.phone, "Italia"); if (n) setNewRecip((p) => ({ ...p, phone: n })); }}
                      placeholder="+39 348 1234567" className="h-9 text-xs" />
                    <Button type="button" data-testid="add-recip-btn" onClick={addExtraRecipient} className="h-9 rounded-full bg-[#25D366] px-3 text-xs font-semibold text-white hover:bg-[#1FB855]">
                      <UserPlus className="mr-1 h-3.5 w-3.5" /> Aggiungi
                    </Button>
                  </div>
                  {extraWaRecipients.length > 0 && (
                    <ul className="mt-2 space-y-1" data-testid="extra-recipients-list">
                      {extraWaRecipients.map((r) => (
                        <li key={r.phone} className="flex items-center justify-between gap-2 rounded border border-border/60 bg-brema-card px-2 py-1 text-xs" data-testid="extra-recipient-row">
                          <span className="flex items-center gap-1.5 truncate text-foreground"><WaLangBadge nation={r.nation} override={bulkLang} /><b>{r.name}</b>{r.city ? ` · ${r.city}` : ""} · {r.phone}</span>
                          <button type="button" onClick={() => removeExtraRecipient(r.phone)} data-testid="remove-recip-btn" className="shrink-0 text-muted-foreground hover:text-red-500"><X className="h-3.5 w-3.5" /></button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
              {bulkChannel === "whatsapp" && !integrations?.whatsapp && (
                <p className="text-xs text-red-500" data-testid="bulk-wa-simulated-note">Invio simulato: i secret WhatsApp non sono ancora configurati.</p>
              )}

              {/* Anteprima destinatari reali */}
              <div className="rounded-lg border border-border bg-brema-bg p-3" data-testid="bulk-recipients-summary">
                <div className="flex items-center justify-between gap-2">
                  <p className="text-sm font-semibold text-foreground" data-testid="bulk-recipients-count">
                    {bulkTotalCount} {bulkTotalCount === 1 ? "destinatario riceverà" : "destinatari riceveranno"} il messaggio{extraWaCount > 0 ? ` (${bulkEligible.length} dall'archivio + ${extraWaCount} aggiunti)` : ""}
                  </p>
                  {bulkEligible.length > 0 && (
                    <button type="button" data-testid="bulk-recipients-toggle" onClick={() => setRecipientsOpen((o) => !o)} className="text-xs font-medium text-brand hover:underline">
                      {recipientsOpen ? "Nascondi elenco" : "Mostra elenco"}
                    </button>
                  )}
                </div>
                {bulkSkippedCount > 0 && (
                  <p className="mt-1 text-xs text-amber-600 dark:text-amber-400" data-testid="bulk-recipients-skipped">
                    {bulkSkippedCount} contatti selezionati verranno saltati (senza {bulkChannel === "whatsapp" ? "numero WhatsApp" : "email valida"}).
                  </p>
                )}
                {recipientsOpen && bulkEligible.length > 0 && (
                  <ul className="mt-2 max-h-44 space-y-1 overflow-y-auto pr-1" data-testid="bulk-recipients-list">
                    {bulkEligible.map((l) => (
                      <li key={l._id} className="flex items-center justify-between gap-2 border-b border-border/50 py-1 text-xs" data-testid="bulk-recipient-row">
                        <span className="truncate font-medium text-foreground">{l.name}</span>
                        <span className="flex items-center gap-1.5 truncate text-muted-foreground">
                          {bulkChannel === "whatsapp" && <WaLangBadge nation={l.nation} override={bulkLang} />}
                          {bulkChannel === "whatsapp" ? (formatE164Display(l.wa_number) || l.phone_e164) : l.email}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div className="flex justify-end gap-2 pt-1">
                <Button variant="secondary" onClick={() => setBulkComposerOpen(false)} className="rounded-full border border-border bg-transparent text-foreground hover:bg-brema-hover">Annulla</Button>
                <Button data-testid="bulk-send-unique-btn" onClick={openBulkConfirm} disabled={bulkSending || bulkTotalCount === 0}
                  className={`rounded-full font-semibold disabled:opacity-40 ${bulkChannel === "whatsapp" ? "bg-[#25D366] text-white hover:bg-[#1FB855]" : "bg-primary text-primary-foreground hover:bg-primary/90"}`}>
                  <Send className="mr-2 h-4 w-4" /> Invia Campagna a {bulkTotalCount} {bulkTotalCount === 1 ? "Destinatario" : "Destinatari"}
                </Button>
              </div>
            </div>
          )}

          {bulkChannel === "email" && bulkMode === "ai" && (
            <div className="space-y-3">
              <div className="space-y-1.5">
                <Label>Tono</Label>
                <Select value={tone} onValueChange={setTone}>
                  <SelectTrigger className="h-9 w-40 text-xs" data-testid="bulk-tone-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="professionale">Professionale</SelectItem>
                    <SelectItem value="amichevole">Amichevole</SelectItem>
                    <SelectItem value="diretto">Diretto</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <p className="text-xs text-muted-foreground">L'AI genererà una bozza personalizzata per ciascuno dei {selectedCount} rivenditori. Potrai rivederle e inviarle dal tab Campagne.</p>
              <div className="flex justify-end gap-2 pt-1">
                <Button variant="secondary" onClick={() => setBulkComposerOpen(false)} className="rounded-full border border-border bg-transparent text-foreground hover:bg-brema-hover">Annulla</Button>
                <Button data-testid="bulk-generate-ai-btn" onClick={runBulkAi} disabled={bulkRunning}
                  className="rounded-full bg-primary font-semibold text-primary-foreground hover:bg-primary/90">
                  {bulkRunning ? (<><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Generazione…</>) : (<><Sparkles className="mr-2 h-4 w-4" /> Genera Bozze AI</>)}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ===== Conferma eliminazione selezionati (protezione) ===== */}
      <Dialog open={deleteSelOpen} onOpenChange={setDeleteSelOpen}>
        <DialogContent className="max-w-md border-border bg-brema-card" data-testid="delete-selected-dialog">
          <DialogHeader>
            <DialogTitle className="text-red-500">Elimina rivenditori selezionati</DialogTitle>
            <DialogDescription>
              Stai per eliminare definitivamente <span className="font-semibold text-foreground">{selectedCount}</span> {selectedCount === 1 ? "rivenditore" : "rivenditori"} selezionati. L'azione è irreversibile e NON tocca gli altri rivenditori.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label className="text-xs text-muted-foreground">Per confermare, scrivi <span className="font-mono font-semibold text-foreground">ELIMINA</span></Label>
            <Input data-testid="delete-selected-confirm-input" value={deleteSelConfirm} onChange={(e) => setDeleteSelConfirm(e.target.value)} placeholder="ELIMINA" />
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="secondary" data-testid="delete-selected-cancel" onClick={() => setDeleteSelOpen(false)} className="rounded-full border border-border bg-transparent text-foreground hover:bg-brema-hover">Annulla</Button>
            <Button data-testid="delete-selected-confirm-btn" onClick={deleteSelected} disabled={deletingSel || deleteSelConfirm.trim().toUpperCase() !== "ELIMINA"}
              className="rounded-full bg-red-600 font-semibold text-white hover:bg-red-700 disabled:opacity-40">
              {deletingSel ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Eliminazione…</> : <><Trash2 className="mr-2 h-4 w-4" /> Elimina {selectedCount}</>}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ===== Conferma invio campagna ===== */}
      <Dialog open={bulkConfirmOpen} onOpenChange={setBulkConfirmOpen}>
        <DialogContent className="max-w-md border-border bg-brema-card" data-testid="bulk-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="text-foreground">Conferma invio campagna</DialogTitle>
            <DialogDescription>
              Sei sicuro di voler inviare questo messaggio a <span className="font-semibold text-foreground">{bulkChannel === "whatsapp" ? bulkTotalCount : bulkEligible.length}</span> {(bulkChannel === "whatsapp" ? bulkTotalCount : bulkEligible.length) === 1 ? "destinatario" : "destinatari"} via {bulkChannel === "whatsapp" ? "WhatsApp" : "Email"}? L'operazione è immediata e non annullabile.
            </DialogDescription>
          </DialogHeader>
          {bulkSkippedCount > 0 && (
            <p className="text-xs text-amber-600 dark:text-amber-400">
              {bulkSkippedCount} contatti selezionati senza {bulkChannel === "whatsapp" ? "numero" : "email"} verranno saltati.
            </p>
          )}
          <div className="flex justify-end gap-2">
            <Button variant="secondary" data-testid="bulk-confirm-cancel" onClick={() => setBulkConfirmOpen(false)} className="rounded-full border border-border bg-transparent text-foreground hover:bg-brema-hover">Annulla</Button>
            <Button data-testid="bulk-confirm-send" onClick={runBulkUnique} disabled={bulkSending}
              className={`rounded-full font-semibold ${bulkChannel === "whatsapp" ? "bg-[#25D366] text-white hover:bg-[#1FB855]" : "bg-primary text-primary-foreground hover:bg-primary/90"}`}>
              {bulkSending ? (<><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Invio…</>) : (<><Send className="mr-2 h-4 w-4" /> Conferma e invia</>)}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ===== Salva come Modello ===== */}
      <Dialog open={saveTplOpen} onOpenChange={setSaveTplOpen}>
        <DialogContent className="max-w-md border-border bg-brema-card" data-testid="save-template-dialog">
          <DialogHeader>
            <DialogTitle className="text-foreground">Salva nella Libreria Modelli</DialogTitle>
            <DialogDescription>Il testo attuale sarà salvato come modello {bulkChannel === "whatsapp" ? "WhatsApp" : "Email"} riutilizzabile.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label>Titolo del modello *</Label>
            <Input data-testid="save-template-title" value={saveTplTitle} onChange={(e) => setSaveTplTitle(e.target.value)} placeholder="Es. Presentazione collezione 2026" />
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setSaveTplOpen(false)} className="rounded-full border border-border bg-transparent text-foreground hover:bg-brema-hover">Annulla</Button>
            <Button data-testid="save-template-confirm" onClick={saveCurrentAsTemplate} disabled={saveTplSaving} className="rounded-full bg-primary font-semibold text-primary-foreground hover:bg-primary/90">
              {saveTplSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Salva modello"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ===== Batch detail dialog ===== */}
      <Dialog open={!!activeBatch} onOpenChange={(o) => !o && setActiveBatch(null)}>
        <DialogContent className="max-w-3xl border-border bg-brema-card" data-testid="batch-detail-dialog">
          <DialogHeader>
            <DialogTitle className="text-foreground">{activeBatch?.title}</DialogTitle>
            <DialogDescription>
              {activeBatch?.recipient_count} destinatari · {activeBatch?.sent_count || 0} inviate · creata il {fmtDateTime(activeBatch?.created_at)}
            </DialogDescription>
          </DialogHeader>
          {batchLoading ? (
            <div className="py-10 text-center text-muted-foreground"><Loader2 className="mx-auto h-6 w-6 animate-spin" /></div>
          ) : (
            <>
              {activeBatch?.channel !== "whatsapp" && batchMessages.some((m) => m.status !== "inviata") && (
                <div className="space-y-3 border-b border-border pb-3" data-testid="batch-global-extras">
                  <BatchCcBcc batch={activeBatch} testidPrefix="sales-batch" />
                  <BatchAttachments batch={activeBatch} testidPrefix="sales-batch" />
                </div>
              )}
              <div className="max-h-[55vh] space-y-3 overflow-y-auto pr-1" data-testid="batch-messages">
                {batchMessages.length === 0 ? (
                  <p className="py-8 text-center text-sm text-muted-foreground" data-testid="batch-empty">Nessuna bozza in questa campagna.</p>
                ) : batchMessages.map((m, idx) => {
                  const sent = m.status === "inviata";
                  return (
                    <div key={m._id} data-testid="batch-message-row" className="rounded-lg border border-border bg-brema-bg p-4">
                      <div className="mb-2 flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-foreground">{m.lead_name}</span>
                          {m.recipient && <span className="text-xs text-muted-foreground">{m.recipient}</span>}
                        </div>
                        <div className="flex items-center gap-1.5">
                          {m.channel === "whatsapp"
                            ? <span className="inline-flex items-center gap-1 rounded-full bg-[#25D366]/15 px-2 py-0.5 text-[10px] font-medium text-[#128C4A] dark:text-[#25D366]" data-testid="msg-channel-badge"><MessageCircle className="h-2.5 w-2.5" /> WhatsApp</span>
                            : <span className="inline-flex items-center gap-1 rounded-full bg-sky-500/15 px-2 py-0.5 text-[10px] font-medium text-sky-700 dark:text-sky-300" data-testid="msg-channel-badge"><Mail className="h-2.5 w-2.5" /> Email</span>}
                          {sent
                            ? <span className="rounded-full bg-positive/15 px-2 py-0.5 text-[11px] font-medium text-positive" data-testid="msg-sent-badge">Inviata ✓</span>
                            : <span className="rounded-full bg-muted px-2 py-0.5 text-[11px] text-foreground">Bozza</span>}
                        </div>
                      </div>
                      {m.channel === "whatsapp" ? (
                        <div className="space-y-2">
                          <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                            <div>
                              <Label className="mb-1 block text-[11px] font-semibold text-muted-foreground">Numero WhatsApp</Label>
                              <Input value={m.wa_phone || m.recipient || ""} data-testid="msg-wa-phone-input" disabled={sent}
                                onChange={(e) => updateMsgField(idx, "wa_phone", e.target.value)} className="disabled:opacity-70" placeholder="+39…" />
                            </div>
                            <div>
                              <Label className="mb-1 block text-[11px] font-semibold text-muted-foreground">{"{{1}} Nome"}</Label>
                              <Input value={(m.wa_params || [])[0] || ""} data-testid="msg-wa-name-input" disabled={sent}
                                onChange={(e) => updateWaParam(idx, 0, e.target.value)} className="disabled:opacity-70" />
                            </div>
                            <div>
                              <Label className="mb-1 block text-[11px] font-semibold text-muted-foreground">{"{{2}} Link"}</Label>
                              <Input value={(m.wa_params || [])[1] || ""} data-testid="msg-wa-link-input" disabled={sent}
                                onChange={(e) => updateWaParam(idx, 1, e.target.value)} className="disabled:opacity-70" />
                            </div>
                          </div>
                          <div className="whitespace-pre-line rounded-lg border border-border bg-brema-card p-3 text-xs text-foreground" data-testid="msg-wa-preview">
                            {`Ciao ${((m.wa_params || [])[0] || "Rivenditore")}! Siamo Brema Bike, brand italiano di abbigliamento tecnico premium. Scopri il catalogo qui: ${((m.wa_params || [])[1] || "")}`}
                          </div>
                          {m.wa_document?.filename && (
                            <div className="flex items-center gap-1.5 text-xs text-muted-foreground" data-testid="msg-wa-doc">
                              <Paperclip className="h-3.5 w-3.5 text-[#25D366]" /> Allegato: {m.wa_document.filename}
                            </div>
                          )}
                        </div>
                      ) : (
                        <>
                          <Label className="mb-1 block text-[11px] font-semibold text-muted-foreground">Oggetto Email:</Label>
                          <Input value={m.subject || ""} data-testid="msg-subject-input" disabled={sent}
                            onChange={(e) => updateMsgField(idx, "subject", e.target.value)}
                            className="mb-2 disabled:opacity-70" placeholder="Scrivi qui l'oggetto dell'email" />
                          <Textarea value={m.email_body || ""} data-testid="msg-body-textarea" disabled={sent}
                            onChange={(e) => updateMsgField(idx, "email_body", e.target.value)}
                            className="min-h-[120px] disabled:opacity-70" placeholder="Corpo email" />
                          {!sent && (
                            (msgCcOpen[m._id] || m.cc || m.bcc) ? (
                              <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2">
                                <div className="space-y-1">
                                  <Label className="text-[11px] text-muted-foreground">CC (solo questa email)</Label>
                                  <Input value={m.cc || ""} data-testid="msg-cc-input" onChange={(e) => updateMsgField(idx, "cc", e.target.value)} className="h-8 text-xs" placeholder="collega@brema.it" />
                                </div>
                                <div className="space-y-1">
                                  <Label className="text-[11px] text-muted-foreground">CCN (solo questa email)</Label>
                                  <Input value={m.bcc || ""} data-testid="msg-bcc-input" onChange={(e) => updateMsgField(idx, "bcc", e.target.value)} className="h-8 text-xs" placeholder="archivio@brema.it" />
                                </div>
                              </div>
                            ) : (
                              <button type="button" data-testid="msg-ccbcc-toggle" onClick={() => setMsgCcOpen((s) => ({ ...s, [m._id]: true }))}
                                className="mt-2 text-[11px] font-medium text-brand hover:underline">+ CC / CCN per questa email</button>
                            )
                          )}
                        </>
                      )}
                      {!sent && (
                        <div className="mt-2 flex items-center justify-end gap-2">
                          <Button size="sm" variant="danger" data-testid="msg-remove-btn" onClick={() => removeFromBatch(m)}
                            className="mr-auto rounded-full text-xs font-medium hover:text-danger">
                            <Trash2 className="mr-1 h-3.5 w-3.5" /> Rimuovi bozza
                          </Button>
                          <Button size="sm" variant="secondary" data-testid="msg-save-btn" onClick={() => saveMsg(m)}
                            className="rounded-full border border-border bg-transparent text-xs text-foreground hover:bg-brema-hover">
                            <Save className="mr-1 h-3.5 w-3.5" /> Salva
                          </Button>
                          <Button size="sm" data-testid="msg-send-btn" onClick={() => sendMsg(m, idx)}
                            className="rounded-full bg-primary text-xs font-semibold text-primary-foreground hover:bg-primary/90">
                            <Send className="mr-1 h-3.5 w-3.5" /> Invia
                          </Button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              {batchMessages.some((m) => m.status !== "inviata") && (
                <div className="space-y-3 border-t border-border pt-3">
                  <div className="flex justify-end">
                    <Button onClick={sendWholeBatch} disabled={batchSending} data-testid="send-batch-btn"
                      className="rounded-full bg-primary font-semibold text-primary-foreground hover:bg-primary/90">
                      {batchSending ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Invio…</> : <><Send className="mr-2 h-4 w-4" /> Invia tutte le bozze</>}
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
